import React, { useState, useMemo, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, Modal, Animated, Platform, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../../firebase';

const GiftTracking = ({ 
	purchasedItems = [], 
	contributions = [], 
	sentThankYouCards = [],
	onSendBulkThankYou,
	formatDate, 
	formatCurrency,
	isMobile,
	senderName,
	contributionGoal: initialGoal = 0, // Default to 0, actual goal loaded from goalTracker
	amountContributed: passedAmountContributed = 0, // From goalTracker - authoritative source
	onUpdateContributionGoal,
	onAddManualContribution,
	onSaveGiftNote,
	giftNotes = {},
	eventId, // For share URL
	onSharePage, // Callback to open share modal
	onScrollToWidget, // Callback to scroll to widget
	totalItems = 0 // Total registry items for accurate remaining count
}) => {
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [searchQuery, setSearchQuery] = useState('');
	const [filterType, setFilterType] = useState('all');
	const [isSending, setIsSending] = useState(false);
	const [hoveredRow, setHoveredRow] = useState(null);
	// Quick-send UI state
	const [quickMessages, setQuickMessages] = useState({});
	const [quickSendingKey, setQuickSendingKey] = useState(null);
	const [quickOpenKey, setQuickOpenKey] = useState(null);
	
	// Quick message helpers
	const handleQuickMessageChange = (key, text) => setQuickMessages(prev => ({ ...prev, [key]: text }));

	const getCardsForGifter = (gifter) => {
		if (!gifter) return [];
		const email = gifter.email;
		const phone = gifter.phone;
		return sentThankYouCards.filter(card => {
			const emailRecipients = card.emailRecipients || (card.recipientEmail ? [card.recipientEmail] : []);
			const smsRecipients = card.smsRecipients || (card.recipientPhone ? [card.recipientPhone] : []);
			const emailMatch = email && emailRecipients.some(e => e.toLowerCase() === email.toLowerCase());
			const phoneMatch = phone && smsRecipients.some(p => p.replace(/\D/g, '') === phone.replace(/\D/g, ''));
			return emailMatch || phoneMatch;
		});
	};

	const handleQuickSend = async (gifter, e) => {
		if (e && e.stopPropagation) e.stopPropagation();
		const key = gifter.gifterKey;
		const msg = (quickMessages[key] || '').trim();
		if (!msg) {
			showToastNotification('Please enter a quick message');
			return;
		}
		setQuickSendingKey(key);
		try {
			const emailRecipients = gifter.email ? [gifter.email] : [];
			const smsRecipients = (!gifter.email && gifter.phone) ? [gifter.phone] : [];
			await onSendBulkThankYou({
				message: msg,
				emailRecipients,
				smsRecipients,
				senderName,
				giftIds: gifter.gifts.map(g => g.id),
				personalizedMessages: [{ email: gifter.email || null, phone: gifter.phone || null, message: msg, name: gifter.name }]
			});
			showToastNotification(`✅ Thank you sent to ${gifter.name}!`);
			setQuickMessages(prev => ({ ...prev, [key]: '' }));
			setQuickOpenKey(null);
		} catch (err) {
			showToastNotification('❌ Failed to send. Please try again.');
		} finally {
			setQuickSendingKey(null);
		}
	};

	// Page-level animation values
	const pageAnim = useRef(new Animated.Value(0)).current;
	const pageSlideAnim = useRef(new Animated.Value(30)).current;
	const shareButtonScale = useRef(new Animated.Value(1)).current;
	const yearbookButtonScale = useRef(new Animated.Value(1)).current;
	const giftsButtonScale = useRef(new Animated.Value(1)).current;
	const goalButtonScale = useRef(new Animated.Value(1)).current;
	const sendCardsButtonScale = useRef(new Animated.Value(1)).current;
	const bottomSendScale = useRef(new Animated.Value(1)).current;
	
	const quickSendAnims = useRef({});
	const getQuickSendAnim = (key) => {
		if (!quickSendAnims.current[key]) {
			quickSendAnims.current[key] = {
				scale: new Animated.Value(1),
				opacity: new Animated.Value(1),
			};
		}
		return quickSendAnims.current[key];
	};
	
	// Hover states
	const [shareHovered, setShareHovered] = useState(false);
	const [yearbookHovered, setYearbookHovered] = useState(false);
	const [giftsHovered, setGiftsHovered] = useState(false);
	const [goalHovered, setGoalHovered] = useState(false);
	const [sendCardsHovered, setSendCardsHovered] = useState(false);
	const [sendThankYouHovered, setSendThankYouHovered] = useState(null); // Track by gifter key
	const [bottomSendHovered, setBottomSendHovered] = useState(false);
	const [modalSendHovered, setModalSendHovered] = useState(false);
	const [modalCancelHovered, setModalCancelHovered] = useState(false);
	const [sendAllHovered, setSendAllHovered] = useState(false);
	
	// Page entrance animation
	useEffect(() => {
		Animated.parallel([
			Animated.timing(pageAnim, {
				toValue: 1,
				duration: 500,
				useNativeDriver: true
			}),
			Animated.timing(pageSlideAnim, {
				toValue: 0,
				duration: 500,
				useNativeDriver: true
			})
		]).start();
	}, []);
	
	// Quick send animation
	useEffect(() => {
		if (quickOpenKey) {
			const anim = getQuickSendAnim(quickOpenKey);
			anim.scale.setValue(0.9);
			anim.opacity.setValue(0);
			Animated.parallel([
				Animated.spring(anim.scale, {
					toValue: 1,
					friction: 8,
					useNativeDriver: true
				}),
				Animated.timing(anim.opacity, {
					toValue: 1,
					duration: 200,
					useNativeDriver: true
				})
			]).start();
		}
	}, [quickOpenKey]);
	
	// Button hover animation helper
	const handleButtonHover = (animValue, isEntering) => {
		Animated.spring(animValue, {
			toValue: isEntering ? 1.05 : 1,
			friction: 8,
			useNativeDriver: true
		}).start();
	};
	
	// Unified Thank You Modal state
	const [showThankYouModal, setShowThankYouModal] = useState(false);
	const [thankYouRecipients, setThankYouRecipients] = useState([]); // Array of recipient objects
	const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
	const [thankYouMessage, setThankYouMessage] = useState('');
	
	const FALLBACK_MESSAGE_TEMPLATES = [
		{ id: 'heartfelt', name: 'Heartfelt', message: 'Dear {{name}},\n\nThank you so much for your incredibly generous gift! Your thoughtfulness and kindness mean the world to me. I\'m so grateful to have you in my life.\n\nWith love and appreciation' },
		{ id: 'casual', name: 'Casual', message: 'Hey {{name}}!\n\nJust wanted to say a huge thank you for the amazing gift! You really didn\'t have to, but I\'m so glad you did. You\'re the best!\n\nThanks again' },
		{ id: 'short', name: 'Short & Sweet', message: 'Dear {{name}},\n\nThank you for your generous gift! Your kindness is truly appreciated.\n\nWarmly' },
		{ id: 'formal', name: 'Formal', message: 'Dear {{name}},\n\nI wanted to extend my sincere gratitude for your thoughtful gift. Your generosity is deeply appreciated and will be cherished.\n\nWith heartfelt thanks' },
	];
	const [messageTemplates, setMessageTemplates] = useState(FALLBACK_MESSAGE_TEMPLATES);

	useEffect(() => {
		const fetchTemplates = async () => {
			try {
				const dbRef = getFirestore(app);
				const snap = await getDoc(doc(dbRef, 'config', 'thankYouCardTemplates'));
				if (snap.exists() && snap.data().templates?.length) {
					setMessageTemplates(snap.data().templates);
				}
			} catch (err) {
				console.warn('Could not fetch thank you card templates, using defaults');
			}
		};
		fetchTemplates();
	}, []);
	
	// Legacy state for backwards compatibility
	const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
	const [bulkMessage, setBulkMessage] = useState('');
	const [showIndividualModal, setShowIndividualModal] = useState(false);
	const [selectedGiftForThankYou, setSelectedGiftForThankYou] = useState(null);
	const [individualMessage, setIndividualMessage] = useState('');
	
	// Manual contribution modal state
	const [showManualContributionModal, setShowManualContributionModal] = useState(false);
	const [manualContributionType, setManualContributionType] = useState('cash'); // 'cash' or 'gift'
	const [manualContributionData, setManualContributionData] = useState({
		contributorName: '',
		contributorEmail: '',
		contributorPhone: '',
		amount: '',
		itemName: '',
		itemDescription: '',
		message: ''
	});
	const [isAddingContribution, setIsAddingContribution] = useState(false);
	const [showResendConfirm, setShowResendConfirm] = useState(false);
	const [pendingResendGift, setPendingResendGift] = useState(null);
	
	// Contribution goal state
	const [contributionGoal, setContributionGoal] = useState(initialGoal);
	const [isEditingGoal, setIsEditingGoal] = useState(false);
	const [goalInput, setGoalInput] = useState(String(initialGoal));
	
	// Sync contributionGoal with prop changes (from parent loading goalTracker data)
	useEffect(() => {
		if (initialGoal && initialGoal !== contributionGoal) {
			setContributionGoal(initialGoal);
			setGoalInput(String(initialGoal));
		}
	}, [initialGoal]);
	
	// Filter state
	
	// Sort state
	const [sortBy, setSortBy] = useState('date');
	const [showSortDropdown, setShowSortDropdown] = useState(false);
	
	// Shift-click selection
	const [lastClickedIndex, setLastClickedIndex] = useState(null);
	
	// Gift notes
	const [editingNoteId, setEditingNoteId] = useState(null);
	const [noteInput, setNoteInput] = useState('');
	
	// Preview modal
	const [showPreviewModal, setShowPreviewModal] = useState(false);
	const [previewRecipients, setPreviewRecipients] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(0);

	// Sent messages modal state
	const [showSentModal, setShowSentModal] = useState(false);
	const [sentModalCards, setSentModalCards] = useState([]);
	const [sentModalGifter, setSentModalGifter] = useState(null);
	
	// Toast notification
	const [showToast, setShowToast] = useState(false);
	const [toastMessage, setToastMessage] = useState('');
	const toastOpacity = useRef(new Animated.Value(0)).current;
	
	// Animated values for progress circles
	const thankYouProgressAnim = useRef(new Animated.Value(0)).current;
	const contributionProgressAnim = useRef(new Animated.Value(0)).current;
	
	// Animated values for decorative bubbles
	const bubble1Anim = useRef(new Animated.Value(0)).current;
	const bubble2Anim = useRef(new Animated.Value(0)).current;
	const bubble3Anim = useRef(new Animated.Value(0)).current;

	// Color palette - MyTassel themed with cream tones
	const colors = {
		primary: '#4c956c',
		primaryLight: 'rgba(76, 149, 108, 0.15)',
		primaryDark: '#2d5a3f',
		secondary: '#6366f1',
		secondaryLight: 'rgba(99, 102, 241, 0.15)',
		warning: '#d97706',
		warningLight: 'rgba(217, 119, 6, 0.15)',
		success: '#059669',
		successLight: 'rgba(5, 150, 105, 0.15)',
		danger: '#dc2626',
		dangerLight: 'rgba(220, 38, 38, 0.15)',
		cream: '#fefde2',
		creamDark: '#f5f0e1',
		creamLight: 'rgba(254, 253, 226, 0.95)',
		text: '#2c3e50',
		textLight: '#5a6b63',
		textMuted: '#7c8a82',
		border: 'rgba(76, 149, 108, 0.15)',
		white: '#ffffff',
	};

	// Font families
	const fonts = {
		heading: 'Bodoni72Smallcaps',
		body: 'Orpheus Pro',
	};

	// Sort options
	const sortOptions = [
		{ key: 'date', label: 'Date (Newest)', icon: 'calendar' },
		{ key: 'dateOld', label: 'Date (Oldest)', icon: 'calendar-outline' },
		{ key: 'amountHigh', label: 'Amount (High to Low)', icon: 'trending-up' },
		{ key: 'amountLow', label: 'Amount (Low to High)', icon: 'trending-down' },
		{ key: 'name', label: 'Name (A-Z)', icon: 'text' },
		{ key: 'status', label: 'Status (Pending First)', icon: 'time' }
	];

	// Combine purchases and contributions into unified gift list
	const allGifts = useMemo(() => {
		const gifts = [];

		purchasedItems.forEach((item, index) => {
			const purchaser = typeof item.purchaser === 'object' ? item.purchaser : { name: item.purchaser };
			gifts.push({
				id: `purchase-${index}`,
				type: 'purchase',
				name: purchaser?.name || 'Anonymous',
				email: purchaser?.email || '',
				phone: purchaser?.phone || '',
				photoURL: purchaser?.photoURL || purchaser?.profilePictureURL || purchaser?.profilePhoto || '',
				giftDescription: item.name || 'Registry Item',
				amount: '', // Registry items don't show dollar amounts
				date: item.purchaseDate || item.purchaseTime,
				message: item.giftMessage || '',
				isAnonymous: item.isAnonymousPurchase || !purchaser?.name
			});
		});

		contributions.forEach((contrib, index) => {
			gifts.push({
				id: contrib.id || `contribution-${index}`,
				type: 'contribution',
				name: contrib.contributor || contrib.contributorName || 'Anonymous',
				email: contrib.contributorEmail || '',
				phone: contrib.contributorPhone || '',
				photoURL: contrib.contributorPhotoURL || contrib.photoURL || '',
				giftDescription: 'Cash Gift',
				amount: typeof contrib.amount === 'number' ? `$${contrib.amount.toFixed(2)}` : contrib.amount,
				date: contrib.contributionDate || contrib.timestamp,
				message: contrib.message || '',
				isAnonymous: contrib.isAnonymous || !contrib.contributor
			});
		});

		return gifts.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
	}, [purchasedItems, contributions]);

	// Safely parse a date from various formats, returns null if invalid
	const safeParseDate = (dateValue) => {
		if (!dateValue) return null;
		// Firebase timestamp object
		if (dateValue?.seconds) return new Date(dateValue.seconds * 1000);
		// Already a Date object
		if (dateValue instanceof Date) return isNaN(dateValue.getTime()) ? null : dateValue;
		// ISO string or timestamp number
		const parsed = new Date(dateValue);
		if (!isNaN(parsed.getTime())) return parsed;
		// Try parsing formatted strings like "Sunday, February 15, 2026 at 10:41:32 AM"
		if (typeof dateValue === 'string') {
			const cleaned = dateValue.replace(/\bat\b/g, '').replace(/,/g, '').replace(/\s+/g, ' ').trim();
			const retried = new Date(cleaned);
			if (!isNaN(retried.getTime())) return retried;
		}
		return null;
	};

	// Check if thank you was sent for this specific gift
	// A gift is only "thanked" if a thank you card was sent AFTER the gift date
	// If dates can't be parsed, falls back to matching by recipient only
	const getThankYouStatus = (gift) => {
		if (!gift.email && !gift.phone) return { sent: false, canSend: false };
		
		const giftDate = safeParseDate(gift.date);
		
		const sent = sentThankYouCards.some(card => {
			// Check if recipient matches
			const emailMatch = gift.email && card.recipientEmail?.toLowerCase() === gift.email.toLowerCase();
			const phoneMatch = gift.phone && card.recipientPhone?.replace(/\D/g, '') === gift.phone.replace(/\D/g, '');
			if (!emailMatch && !phoneMatch) return false;
			
			// If we have both valid dates, only count as thanked if the card was sent after the gift
			const cardDate = safeParseDate(card.sentDate);
			if (giftDate && cardDate) {
				return cardDate >= giftDate;
			}
			
			// If either date can't be parsed, fall back to considering it thanked (recipient matched)
			return true;
		});

		return { sent, canSend: !!(gift.email || gift.phone) };
	};

	// Filter gifts
	const filteredGifts = useMemo(() => {
		let result = [...allGifts];

		// Filter by type
		if (filterType === 'new') {
			const weekAgo = new Date();
			weekAgo.setDate(weekAgo.getDate() - 7);
			result = result.filter(g => new Date(g.date || 0) >= weekAgo);
		} else if (filterType === 'pending') {
			result = result.filter(g => {
				const status = getThankYouStatus(g);
				return status.canSend && !status.sent;
			});
		} else if (filterType === 'ready') {
			result = result.filter(g => {
				const status = getThankYouStatus(g);
				return status.sent;
			});
		} else if (filterType === 'hidden') {
			result = result.filter(g => g.isAnonymous || g.name === 'Anonymous');
		}

		// Search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			result = result.filter(g => 
				g.name.toLowerCase().includes(query) ||
				g.email.toLowerCase().includes(query) ||
				g.giftDescription.toLowerCase().includes(query)
			);
		}

		// Sort
		const parseAmount = (g) => parseFloat((g.amount || '0').replace(/[^0-9.]/g, ''));
		
		switch (sortBy) {
			case 'dateOld':
				result.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
				break;
			case 'amountHigh':
				result.sort((a, b) => parseAmount(b) - parseAmount(a));
				break;
			case 'amountLow':
				result.sort((a, b) => parseAmount(a) - parseAmount(b));
				break;
			case 'name':
				result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
				break;
			case 'status':
				result.sort((a, b) => {
					const statusA = getThankYouStatus(a);
					const statusB = getThankYouStatus(b);
					// Pending first (canSend && !sent), then sent, then can't send
					const scoreA = statusA.canSend ? (statusA.sent ? 1 : 0) : 2;
					const scoreB = statusB.canSend ? (statusB.sent ? 1 : 0) : 2;
					return scoreA - scoreB;
				});
				break;
			case 'date':
			default:
				result.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
				break;
		}

		return result;
	}, [allGifts, filterType, searchQuery, sentThankYouCards, sortBy]);

	// Group gifts by gifter (name + email combination)
	// Anonymous users are NOT grouped - each anonymous gift is its own entry
	const groupedGifts = useMemo(() => {
		const groups = {};
		let anonymousCounter = 0;
		
		filteredGifts.forEach(gift => {
			// Check if this is an anonymous gift - don't group these
			const isAnon = gift.isAnonymous || gift.name === 'Anonymous' || !gift.name;
			
			// Create a unique key - anonymous gifts get unique keys
			let gifterKey;
			if (isAnon) {
				// Each anonymous gift gets its own unique key
				gifterKey = `anonymous-${gift.id}-${anonymousCounter++}`;
			} else {
				// Group by name + email (or name + phone if no email)
				gifterKey = gift.email 
					? `${gift.name.toLowerCase()}-${gift.email.toLowerCase()}`
					: `${gift.name.toLowerCase()}-no-email-${gift.phone || 'no-phone'}`;
			}
			
			if (!groups[gifterKey]) {
				groups[gifterKey] = {
					gifterKey,
					name: gift.name || 'Anonymous',
					email: gift.email,
					phone: gift.phone,
					photoURL: gift.photoURL,
					isAnonymous: isAnon,
					gifts: [],
					totalAmount: 0,
					latestDate: gift.date,
					// Track if any gift needs thank you
					hasUnsent: false,
					hasSent: false,
					canSend: false
				};
			}
			
			groups[gifterKey].gifts.push(gift);
			
			// Parse and add to total amount
			const amount = parseFloat((gift.amount || '0').replace(/[^0-9.]/g, ''));
			groups[gifterKey].totalAmount += amount;
			
			// Update latest date
			if (new Date(gift.date) > new Date(groups[gifterKey].latestDate)) {
				groups[gifterKey].latestDate = gift.date;
			}
			
			// Update thank you status
			const status = getThankYouStatus(gift);
			if (status.canSend) {
				groups[gifterKey].canSend = true;
				if (status.sent) {
					groups[gifterKey].hasSent = true;
				} else {
					groups[gifterKey].hasUnsent = true;
				}
			}
		});
		
		// Convert to array and sort by latest date
		return Object.values(groups).sort((a, b) => 
			new Date(b.latestDate || 0) - new Date(a.latestDate || 0)
		);
	}, [filteredGifts, sentThankYouCards]);

	// Count anonymous gifts for filter display
	const anonymousCount = allGifts.filter(g => g.isAnonymous || g.name === 'Anonymous').length;

	// Stats
	const totalGifts = allGifts.length;
	
	// Count unique gifters who need thank yous (not individual items)
	// This matches the grouped display in gift history
	const needsThankYouCount = useMemo(() => {
		const giftersWithPending = new Set();
		let anonymousCounter = 0;
		
		allGifts.forEach(gift => {
			const status = getThankYouStatus(gift);
			if (status.canSend && !status.sent) {
				// Check if anonymous
				const isAnon = gift.isAnonymous || gift.name === 'Anonymous' || !gift.name;
				
				if (isAnon) {
					// Each anonymous gift counts as separate gifter
					giftersWithPending.add(`anonymous-${gift.id}-${anonymousCounter++}`);
				} else {
					// Group by name + email (or name + phone)
					const gifterKey = gift.email 
						? `${gift.name.toLowerCase()}-${gift.email.toLowerCase()}`
						: `${gift.name.toLowerCase()}-no-email-${gift.phone || 'no-phone'}`;
					giftersWithPending.add(gifterKey);
				}
			}
		});
		
		return giftersWithPending.size;
	}, [allGifts, sentThankYouCards]);
	
	// Count unique gifters who have received thank yous (not individual items)
	const thankYouSentCount = useMemo(() => {
		const giftersWithSent = new Set();
		let anonymousCounter = 0;
		
		allGifts.forEach(gift => {
			const status = getThankYouStatus(gift);
			if (status.sent) {
				const isAnon = gift.isAnonymous || gift.name === 'Anonymous' || !gift.name;
				
				if (isAnon) {
					giftersWithSent.add(`anonymous-${gift.id}-${anonymousCounter++}`);
				} else {
					const gifterKey = gift.email 
						? `${gift.name.toLowerCase()}-${gift.email.toLowerCase()}`
						: `${gift.name.toLowerCase()}-no-email-${gift.phone || 'no-phone'}`;
					giftersWithSent.add(gifterKey);
				}
			}
		});
		
		return giftersWithSent.size;
	}, [allGifts, sentThankYouCards]);
	
	// Count total unique gifters who CAN be thanked (have email or phone)
	// This excludes anonymous/no-contact gifters from the percentage calculation
	const totalThankableGifters = useMemo(() => {
		const thankableGifters = new Set();
		
		allGifts.forEach(gift => {
			// Only count gifters who have contact info (can be thanked)
			if (!gift.email && !gift.phone) return;
			
			const isAnon = gift.isAnonymous || gift.name === 'Anonymous' || !gift.name;
			
			if (isAnon) {
				// Anonymous with contact info - each is unique
				thankableGifters.add(`anonymous-${gift.id}`);
			} else {
				const gifterKey = gift.email 
					? `${gift.name.toLowerCase()}-${gift.email.toLowerCase()}`
					: `${gift.name.toLowerCase()}-no-email-${gift.phone || 'no-phone'}`;
				thankableGifters.add(gifterKey);
			}
		});
		
		return thankableGifters.size;
	}, [allGifts]);
	
	// Completion percent based on thankable gifters only
	// If 1 thanked and 0 pending = 100% (not based on total gifters including anonymous)
	const completionPercent = totalThankableGifters > 0 ? Math.round((thankYouSentCount / totalThankableGifters) * 100) : 0;

	// Calculate total amount
	const totalAmount = allGifts.reduce((sum, g) => {
		const amount = parseFloat((g.amount || '0').replace(/[^0-9.]/g, ''));
		return sum + amount;
	}, 0);

	// Use passedAmountContributed as the authoritative source (from goalTracker)
	// This ensures consistency with registry page and combined card
	const contributionTotal = passedAmountContributed > 0 
		? passedAmountContributed 
		: contributions.reduce((sum, contrib) => {
			const amount = typeof contrib.amount === 'number' 
				? contrib.amount 
				: parseFloat((contrib.amount || '0').replace(/[^0-9.]/g, ''));
			return sum + amount;
		}, 0);

	// Calculate contribution goal progress
	const contributionProgress = contributionGoal > 0 ? Math.min(100, Math.round((contributionTotal / contributionGoal) * 100)) : 0;
	
	// Handle adding manual contribution
	const handleSubmitManualContribution = async () => {
		if (!onAddManualContribution) return;
		
		setIsAddingContribution(true);
		const result = await onAddManualContribution({
			type: manualContributionType,
			...manualContributionData
		});
		
		setIsAddingContribution(false);
		
		if (result.success) {
			setShowManualContributionModal(false);
			setManualContributionData({
				contributorName: '',
				contributorEmail: '',
				contributorPhone: '',
				amount: '',
				itemName: '',
				itemDescription: '',
				message: ''
			});
			showToastNotification('✓ Contribution added successfully!');
		} else {
			showToastNotification('Failed to add contribution');
		}
	};

	// Handle goal update
	const handleGoalUpdate = () => {
		const newGoal = parseFloat(goalInput.replace(/[^0-9.]/g, ''));
		if (!isNaN(newGoal) && newGoal > 0) {
			setContributionGoal(newGoal);
			if (onUpdateContributionGoal) {
				onUpdateContributionGoal(newGoal);
			}
			showToastNotification('✓ Goal updated successfully');
		}
		setIsEditingGoal(false);
	};

	// Animate progress circles when values change
	useEffect(() => {
		Animated.timing(thankYouProgressAnim, {
			toValue: completionPercent,
			duration: 800,
			useNativeDriver: false,
		}).start();
	}, [completionPercent]);

	useEffect(() => {
		Animated.timing(contributionProgressAnim, {
			toValue: contributionProgress,
			duration: 800,
			useNativeDriver: false,
		}).start();
	}, [contributionProgress]);

	// Floating bubble animations
	useEffect(() => {
		const createBubbleAnimation = (animValue, duration, delay = 0) => {
			return Animated.loop(
				Animated.sequence([
					Animated.timing(animValue, {
						toValue: 1,
						duration: duration,
						delay: delay,
						useNativeDriver: false,
					}),
					Animated.timing(animValue, {
						toValue: 0,
						duration: duration,
						useNativeDriver: false,
					}),
				])
			);
		};
		
		const anim1 = createBubbleAnimation(bubble1Anim, 3000, 0);
		const anim2 = createBubbleAnimation(bubble2Anim, 4000, 500);
		const anim3 = createBubbleAnimation(bubble3Anim, 3500, 1000);
		
		anim1.start();
		anim2.start();
		anim3.start();
		
		return () => {
			anim1.stop();
			anim2.stop();
			anim3.stop();
		};
	}, []);

	// Export to CSV
	const exportToCSV = () => {
		const headers = ['Name', 'Email', 'Phone', 'Gift Type', 'Gift Description', 'Amount', 'Date', 'Message', 'Thank You Sent', 'Notes'];
		const rows = filteredGifts.map(gift => {
			const status = getThankYouStatus(gift);
			const note = giftNotes[gift.id] || '';
			return [
				gift.name,
				gift.email,
				gift.phone,
				gift.type === 'purchase' ? 'Item' : 'Cash Gift',
				gift.giftDescription,
				gift.amount,
				gift.date ? new Date(gift.date).toLocaleDateString() : '',
				gift.message,
				status.sent ? 'Yes' : (status.canSend ? 'No' : 'N/A'),
				note
			].map(val => `"${(val || '').toString().replace(/"/g, '""')}"`).join(',');
		});
		
		const csvContent = [headers.join(','), ...rows].join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', `gift-history-${new Date().toISOString().split('T')[0]}.csv`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		showToastNotification('✓ Gift history exported!');
	};

	// Select all visible
	const selectAllVisible = () => {
		const selectable = filteredGifts.filter(g => {
			const status = getThankYouStatus(g);
			return status.canSend;
		});
		setSelectedIds(new Set(selectable.map(g => g.id)));
	};

	// Handle shift-click range selection
	const handleRowClick = (gift, index, event) => {
		const status = getThankYouStatus(gift);
		if (!status.canSend) return;

		if (event?.shiftKey && lastClickedIndex !== null) {
			// Range selection
			const start = Math.min(lastClickedIndex, index);
			const end = Math.max(lastClickedIndex, index);
			const newSelected = new Set(selectedIds);
			
			for (let i = start; i <= end; i++) {
				const g = filteredGifts[i];
				const s = getThankYouStatus(g);
				if (s.canSend) {
					newSelected.add(g.id);
				}
			}
			setSelectedIds(newSelected);
		} else {
			// Normal toggle
			toggleSelect(gift.id);
		}
		setLastClickedIndex(index);
	};

	// Save gift note
	const handleSaveNote = (giftId) => {
		if (onSaveGiftNote) {
			onSaveGiftNote(giftId, noteInput);
		}
		setEditingNoteId(null);
		setNoteInput('');
		showToastNotification('✓ Note saved');
	};

	// Open preview modal (now uses unified thank you modal)
	const openPreviewModal = () => {
		openThankYouModal();
	};

	// Selection handlers
	const toggleSelect = (id) => {
		const newSelected = new Set(selectedIds);
		if (newSelected.has(id)) {
			newSelected.delete(id);
		} else {
			newSelected.add(id);
		}
		setSelectedIds(newSelected);
	};

	const selectAllPending = () => {
		const pending = filteredGifts.filter(g => {
			const status = getThankYouStatus(g);
			return status.canSend && !status.sent;
		});
		setSelectedIds(new Set(pending.map(g => g.id)));
	};

	const clearSelection = () => setSelectedIds(new Set());

	// Get selected gifts with contact
	const getSelectedGiftsWithContact = () => {
		return filteredGifts.filter(g => selectedIds.has(g.id) && (g.email || g.phone));
	};

	// Get unique recipients grouped by person (for sending ONE thank you per person)
	const getUniqueRecipients = () => {
		const selectedGifts = filteredGifts.filter(g => selectedIds.has(g.id) && (g.email || g.phone));
		const recipientMap = {};
		
		selectedGifts.forEach(gift => {
			// Create unique key by email or phone
			const key = gift.email 
				? gift.email.toLowerCase() 
				: gift.phone;
			
			if (!recipientMap[key]) {
				recipientMap[key] = {
					name: gift.name,
					email: gift.email,
					phone: gift.phone,
					photoURL: gift.photoURL,
					gifts: [],
					totalAmount: 0,
					giftIds: []
				};
			}
			
			recipientMap[key].gifts.push(gift);
			recipientMap[key].giftIds.push(gift.id);
			const amount = parseFloat((gift.amount || '0').replace(/[^0-9.]/g, ''));
			recipientMap[key].totalAmount += amount;
		});
		
		return Object.values(recipientMap);
	};

	// Get unique gifters from selected gifts
	const getUniqueGifters = () => {
		const selectedGifts = filteredGifts.filter(g => selectedIds.has(g.id));
		const uniqueNames = new Set(selectedGifts.map(g => g.name?.toLowerCase() || 'anonymous'));
		return uniqueNames.size;
	};

	// Get single selected gift (when only one selected)
	const getSingleSelectedGift = () => {
		if (selectedIds.size !== 1) return null;
		const id = Array.from(selectedIds)[0];
		return filteredGifts.find(g => g.id === id);
	};

	// Open unified thank you modal for selected gifts or a specific gifter
	const openThankYouModal = (recipients = null) => {
		let recipientsToUse;
		
		if (recipients) {
			// Specific recipients passed in (e.g., from clicking Send Thank You on a gifter card)
			recipientsToUse = Array.isArray(recipients) ? recipients : [recipients];
		} else {
			// Use currently selected gifts
			recipientsToUse = getUniqueRecipients();
		}
		
		if (recipientsToUse.length === 0) return;
		
		setThankYouRecipients(recipientsToUse);
		setSelectedTemplateIndex(0);
		// Single recipient: show personalized preview. Multiple: keep {{name}} so each gets their own.
		if (recipientsToUse.length === 1) {
			const firstName = recipientsToUse[0]?.name?.split(' ')[0] || 'Friend';
			setThankYouMessage(messageTemplates[0].message.replace(/\{\{name\}\}/g, firstName));
		} else {
			setThankYouMessage(messageTemplates[0].message);
		}
		setShowThankYouModal(true);
	};
	
	// Get personalized message for a specific recipient
	const getPersonalizedMessage = (recipient, template) => {
		const firstName = recipient?.name?.split(' ')[0] || 'Friend';
		return template.replace(/\{\{name\}\}/g, firstName);
	};
	
	// Handle selecting a template
	const selectTemplate = (index) => {
		setSelectedTemplateIndex(index);
		// Single recipient: personalize preview. Multiple: keep {{name}} placeholder.
		if (thankYouRecipients.length === 1) {
			const firstName = thankYouRecipients[0]?.name?.split(' ')[0] || 'Friend';
			setThankYouMessage(messageTemplates[index].message.replace(/\{\{name\}\}/g, firstName));
		} else {
			setThankYouMessage(messageTemplates[index].message);
		}
	};
	
	// Handle unified send - sends personalized messages to each unique recipient
	const handleUnifiedSend = async () => {
		if (thankYouRecipients.length === 0 || !thankYouMessage.trim()) return;

		setIsSending(true);
		try {
			// For each recipient, personalize the message with their name
			const emailRecipients = [];
			const smsRecipients = [];
			const allGiftIds = [];
			const personalizedMessages = [];
			
			thankYouRecipients.forEach(recipient => {
			const firstName = recipient.name?.split(' ')[0] || 'Friend';
			// Replace {{name}} placeholder with this recipient's first name for personalization
			const personalizedMsg = thankYouMessage.replace(/\{\{name\}\}/g, firstName);
			
			if (recipient.email) {
				emailRecipients.push({ email: recipient.email, message: personalizedMsg, name: recipient.name });
			} else if (recipient.phone) {
				smsRecipients.push({ phone: recipient.phone, message: personalizedMsg, name: recipient.name });
			}
			
			allGiftIds.push(...(recipient.giftIds || [recipient.id]));
			personalizedMessages.push({ email: recipient.email || null, phone: recipient.phone || null, message: personalizedMsg, name: recipient.name });
		});

			await onSendBulkThankYou({
				message: thankYouMessage, // Base message
				emailRecipients: emailRecipients.map(r => r.email),
				smsRecipients: smsRecipients.map(r => r.phone),
				senderName,
				giftIds: allGiftIds,
				personalizedMessages // For backend to use personalized versions
			});

			setSelectedIds(new Set());
			setThankYouMessage('');
			setShowThankYouModal(false);
			setThankYouRecipients([]);
			
			const totalGifts = thankYouRecipients.reduce((sum, r) => sum + (r.gifts?.length || 1), 0);
			showToastNotification(`✓ Thank you sent to ${thankYouRecipients.length} recipient${thankYouRecipients.length !== 1 ? 's' : ''}!`);
		} catch (error) {
			alert('Failed to send. Please try again.');
		} finally {
			setIsSending(false);
		}
	};

	// Legacy: Handle bulk send (redirects to unified modal)
	const handleBulkSend = async () => {
		openThankYouModal();
	};

	// Toast notification
	const showToastNotification = (message) => {
		setToastMessage(message);
		setShowToast(true);
		
		Animated.sequence([
			Animated.timing(toastOpacity, {
				toValue: 1,
				duration: 300,
				useNativeDriver: true,
			}),
			Animated.delay(2500),
			Animated.timing(toastOpacity, {
				toValue: 0,
				duration: 300,
				useNativeDriver: true,
			}),
		]).start(() => setShowToast(false));
	};

	// Handle clicking "Send Thank You" on individual row
	const handleIndividualThankYou = (gift) => {
		const status = getThankYouStatus(gift);
		
		if (status.sent) {
			// Already sent - show confirmation
			setPendingResendGift(gift);
			setShowResendConfirm(true);
		} else {
			// First time - open modal directly
			openIndividualModal(gift);
		}
	};

	// Open the individual thank you modal (now uses unified modal)
	const openIndividualModal = (gift) => {
		// Convert single gift to recipient format for unified modal
		const recipient = {
			name: gift.name,
			email: gift.email,
			phone: gift.phone,
			photoURL: gift.photoURL,
			gifts: [gift],
			giftIds: [gift.id],
			totalAmount: parseFloat((gift.amount || '0').replace(/[^0-9.]/g, ''))
		};
		openThankYouModal([recipient]);
	};

	// Confirm re-send and open modal
	const confirmResend = () => {
		if (pendingResendGift) {
			openIndividualModal(pendingResendGift);
		}
		setShowResendConfirm(false);
		setPendingResendGift(null);
	};

	// Cancel re-send
	const cancelResend = () => {
		setShowResendConfirm(false);
		setPendingResendGift(null);
	};

	// Open Sent Messages Modal for a gifter
	const openSentModal = (gifterGroup) => {
		const cards = getCardsForGifter(gifterGroup).slice().sort((a, b) => new Date(b.sentAt || b.sentDate) - new Date(a.sentAt || a.sentDate));
		setSentModalCards(cards);
		setSentModalGifter(gifterGroup);
		setShowSentModal(true);
	};

	const closeSentModal = () => {
		setShowSentModal(false);
		setSentModalCards([]);
		setSentModalGifter(null);
	};

	// Send individual thank you
	const handleSendIndividualThankYou = async () => {
		if (!selectedGiftForThankYou || !individualMessage.trim()) return;

		setIsSending(true);
		try {
			const emailRecipients = selectedGiftForThankYou.email ? [selectedGiftForThankYou.email] : [];
			const smsRecipients = selectedGiftForThankYou.phone && !selectedGiftForThankYou.email 
				? [selectedGiftForThankYou.phone] 
				: [];

			await onSendBulkThankYou({
				message: individualMessage,
				emailRecipients,
				smsRecipients,
				senderName,
				personalizedMessages: [{ email: selectedGiftForThankYou.email || null, phone: selectedGiftForThankYou.phone || null, message: individualMessage, name: selectedGiftForThankYou.name }]
			});

			showToastNotification(`✅ Thank you sent to ${selectedGiftForThankYou.name}!`);
			setShowIndividualModal(false);
			setSelectedGiftForThankYou(null);
			setIndividualMessage('');
		} catch (error) {
			showToastNotification('❌ Failed to send. Please try again.');
		} finally {
			setIsSending(false);
		}
	};

	// Get initials for avatar
	const getInitials = (name) => {
		if (!name || name === 'Anonymous') return '?';
		const parts = name.trim().split(' ');
		if (parts.length >= 2) {
			return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
		}
		return name.substring(0, 2).toUpperCase();
	};

	// Format name as "FirstName L." for display
	const formatShortName = (name) => {
		if (!name || name === 'Anonymous') return 'Anonymous';
		const parts = name.trim().split(' ');
		if (parts.length >= 2) {
			return `${parts[0]} ${parts[parts.length - 1][0]}.`;
		}
		return parts[0];
	};

	// Get selected gift names for display (deduplicated)
	const getSelectedNamesDisplay = (maxChars = 60) => {
		const selectedGifts = filteredGifts.filter(g => selectedIds.has(g.id));
		// Deduplicate names using a Set
		const uniqueNames = [...new Set(selectedGifts.map(g => formatShortName(g.name)))];
		
		let display = '';
		let count = 0;
		
		for (const name of uniqueNames) {
			const separator = count > 0 ? ', ' : '';
			const potential = display + separator + name;
			
			if (potential.length > maxChars && count > 0) {
				const remaining = uniqueNames.length - count;
				return `${display} +${remaining} more`;
			}
			
			display = potential;
			count++;
		}
		
		return display;
	};

	// Get avatar color based on name
	const getAvatarColor = (name) => {
		const avatarColors = [
			{ bg: '#dbeafe', text: '#1e40af' },
			{ bg: '#dcfce7', text: '#166534' },
			{ bg: '#fef3c7', text: '#92400e' },
			{ bg: '#fce7f3', text: '#9d174d' },
			{ bg: '#e0e7ff', text: '#3730a3' },
			{ bg: '#fed7d7', text: '#c53030' },
			{ bg: '#d5f5f6', text: '#0e7490' },
		];
		const index = name ? name.charCodeAt(0) % avatarColors.length : 0;
		return avatarColors[index];
	};

	const selectedCount = selectedIds.size;
	const uniqueRecipients = getUniqueRecipients();

	// Current gift being edited for notes
	const currentNoteGift = useMemo(() => {
		if (!editingNoteId) return null;
		return filteredGifts.find(g => g.id === editingNoteId) || allGifts.find(g => g.id === editingNoteId) || null;
	}, [editingNoteId, filteredGifts, allGifts]);

	return (
		<Animated.View style={{ 
			marginBottom: 32,
			opacity: pageAnim,
			transform: [{ translateY: pageSlideAnim }]
		}}>
			{/* Stats Cards Row - Two Cards */}
			<View style={{
				flexDirection: 'column',
				gap: 24,
				marginBottom: 28
			}}>
				{/* Left Card - Total Gifts (enhanced with gradient feel) */}
				<View style={{
					width: '100%',
					backgroundColor: colors.primary,
					borderRadius: 28,
					padding: isMobile ? 24 : 32,
					minHeight: isMobile ? 'auto' : 220,
					shadowColor: '#1a5c3a',
					shadowOffset: { width: 0, height: 8 },
					shadowOpacity: 0.25,
					shadowRadius: 16,
					elevation: 8,
					overflow: 'hidden',
					position: 'relative'
				}}>
					{/* Animated Decorative Bubbles */}
					<Animated.View style={{
						position: 'absolute',
						top: bubble1Anim.interpolate({
							inputRange: [0, 1],
							outputRange: [-40, -50]
						}),
						right: -40,
						width: 150,
						height: 150,
						borderRadius: 75,
						backgroundColor: 'rgba(255,255,255,0.1)',
						transform: [{
							scale: bubble1Anim.interpolate({
								inputRange: [0, 0.5, 1],
								outputRange: [1, 1.05, 1]
							})
						}]
					}} />
					<Animated.View style={{
						position: 'absolute',
						bottom: bubble2Anim.interpolate({
							inputRange: [0, 1],
							outputRange: [-30, -40]
						}),
						left: -30,
						width: 100,
						height: 100,
						borderRadius: 50,
						backgroundColor: 'rgba(255,255,255,0.08)',
						transform: [{
							scale: bubble2Anim.interpolate({
								inputRange: [0, 0.5, 1],
								outputRange: [1, 1.08, 1]
							})
						}]
					}} />
					<Animated.View style={{
						position: 'absolute',
						top: bubble3Anim.interpolate({
							inputRange: [0, 1],
							outputRange: [60, 50]
						}),
						right: 60,
						width: 60,
						height: 60,
						borderRadius: 30,
						backgroundColor: 'rgba(255,255,255,0.06)',
						transform: [{
							scale: bubble3Anim.interpolate({
								inputRange: [0, 0.5, 1],
								outputRange: [1, 1.1, 1]
							})
						}]
					}} />
					
					{/* Content */}
					<View style={{ position: 'relative', zIndex: 1 }}>
						<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
							<Text style={{ fontSize: 16, fontFamily: fonts.body, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: 1 }}>
								People Celebrating You
							</Text>
							<View style={{
								width: 44,
								height: 44,
								borderRadius: 22,
								backgroundColor: 'rgba(255,255,255,0.2)',
								justifyContent: 'center',
								alignItems: 'center'
							}}>
								<Ionicons name="gift" size={22} color="#fff" />
							</View>
						</View>
						
						<View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
							<Text style={{ fontSize: isMobile ? 56 : 72, fontFamily: fonts.heading, color: '#fff', fontWeight: 'bold' }}>
								{totalGifts}
							</Text>
							<Text style={{ fontSize: 20, fontFamily: fonts.body, color: 'rgba(255,255,255,0.9)' }}>
								gifts from friends & family
							</Text>
						</View>
					
						{/* <View style={{
							backgroundColor: 'rgba(255,255,255,0.2)',
							paddingHorizontal: 16,
							paddingVertical: 8,
							borderRadius: 20,
							alignSelf: 'flex-start',
							marginBottom: 20
						}}>
							<Text style={{ fontSize: 18, fontFamily: fonts.body, fontWeight: '700', color: '#fff' }}>
								${totalAmount.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})} total value
							</Text>
						</View> */}
						
						{/* Gift Items and Total Cash Count */}
						{/* <View style={{ flexDirection: 'row', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
							<View style={{ 
								flexDirection: 'row', 
								alignItems: 'center', 
								gap: 8,
								backgroundColor: 'rgba(255,255,255,0.15)',
								paddingHorizontal: 14,
								paddingVertical: 8,
								borderRadius: 16
							}}>
								<Ionicons name="cube-outline" size={18} color="#fff" />
								<Text style={{ fontSize: 15, fontFamily: fonts.body, color: '#fff', fontWeight: '500' }}>
									{purchasedItems.length} Items
								</Text>
							</View>
							<View style={{ 
								flexDirection: 'row', 
								alignItems: 'center', 
								gap: 8,
								backgroundColor: 'rgba(255,255,255,0.15)',
								paddingHorizontal: 14,
								paddingVertical: 8,
								borderRadius: 16
							}}>
								<Ionicons name="cash-outline" size={18} color="#fff" />
								<Text style={{ fontSize: 15, fontFamily: fonts.body, color: '#fff', fontWeight: '500' }}>
									{contributions.length} Cash
								</Text>
							</View>
						</View> */}
						
						{/* Send Thank You */}
						<View style={{ flexDirection: 'row', gap: 10 }}>
							{/* Send Thank You's Button */}
							<View style={{ marginBottom: 16, width: '100%' }}>
								<Animated.View style={{ flex: 1, transform: [{ scale: bottomSendScale }] }}>
									<TouchableOpacity
										onPress={() => {
											if (needsThankYouCount === 0) {
												if (onScrollToWidget) onScrollToWidget('giftTracking', 400);
											} else {
												// Build pending recipients directly to avoid state timing issues
												const pending = filteredGifts.filter(g => {
													const status = getThankYouStatus(g);
													return status.canSend && !status.sent && (g.email || g.phone);
												});
												const recipientMap = {};
												pending.forEach(gift => {
													const key = gift.email ? gift.email.toLowerCase() : gift.phone;
													if (!recipientMap[key]) {
														recipientMap[key] = { name: gift.name, email: gift.email || '', phone: gift.phone || '', photoURL: gift.photoURL, gifts: [], giftIds: [] };
													}
													recipientMap[key].gifts.push(gift);
													recipientMap[key].giftIds.push(gift.id);
												});
												openThankYouModal(Object.values(recipientMap));
											}
										}}
										onMouseEnter={() => {
											setBottomSendHovered(true);
											handleButtonHover(bottomSendScale, true);
										}}
										onMouseLeave={() => {
											setBottomSendHovered(false);
											handleButtonHover(bottomSendScale, false);
										}}
										activeOpacity={0.9}
										style={{
											flexDirection: 'row',
											alignItems: 'center',
											justifyContent: 'center',
											gap: 6,
											backgroundColor: bottomSendHovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
											paddingVertical: 12,
											paddingHorizontal: 14,
											borderRadius: 12,
											borderWidth: 1,
											borderColor: bottomSendHovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
											...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
										}}
									>
										<Ionicons name={needsThankYouCount > 0 ? "mail-outline" : "gift-outline"} size={18} color="#fff" />
										<Text style={{ fontSize: 14, fontFamily: fonts.body, color: '#fff', fontWeight: '600' }}>
											{needsThankYouCount > 0 ? "Send Thank You's" : "View Gifts & Contributions"}
										</Text>
									</TouchableOpacity>
								</Animated.View>
							</View>
						</View>

						{/* Action Buttons Row */}
						<View style={{ flexDirection: 'row', gap: 10 }}>
							{/* Share Pages Button with Hover */}
							{/* <Animated.View style={{ flex: 1, transform: [{ scale: shareButtonScale }] }}>
								<TouchableOpacity
									onPress={() => {
										if (onSharePage) {
											onSharePage();
										} else {
											// Fallback: Copy registry link to clipboard with eventId
											const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://mytassel.com';
											const registryUrl = eventId 
												? `${baseUrl}/registry?eventId=${eventId}` 
												: `${baseUrl}/registry`;
											if (typeof navigator !== 'undefined' && navigator.clipboard) {
												navigator.clipboard.writeText(registryUrl);
												showToastNotification('✓ Registry link copied!');
											}
										}
									}}
									onMouseEnter={() => {
										setShareHovered(true);
										handleButtonHover(shareButtonScale, true);
									}}
									onMouseLeave={() => {
										setShareHovered(false);
										handleButtonHover(shareButtonScale, false);
									}}
									activeOpacity={0.9}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 6,
										backgroundColor: shareHovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
										paddingVertical: 12,
										paddingHorizontal: 14,
										borderRadius: 12,
										borderWidth: 1,
										borderColor: shareHovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
										...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
									}}
								>
									<Ionicons name="share-outline" size={18} color="#fff" />
									<Text style={{ fontSize: 14, fontFamily: fonts.body, color: '#fff', fontWeight: '600' }}>
										Share
									</Text>
								</TouchableOpacity>
							</Animated.View> */}

							{/* View Yearbook Messages with Hover */}
							<Animated.View style={{ flex: 1.5, transform: [{ scale: yearbookButtonScale }] }}>
								<TouchableOpacity
									onPress={() => onScrollToWidget('yearbookMessages')}
									onMouseEnter={() => {
										setYearbookHovered(true);
										handleButtonHover(yearbookButtonScale, true);
									}}
									onMouseLeave={() => {
										setYearbookHovered(false);
										handleButtonHover(yearbookButtonScale, false);
									}}
									activeOpacity={0.9}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 6,
										backgroundColor: yearbookHovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
										paddingVertical: 12,
										paddingHorizontal: 14,
										borderRadius: 12,
										borderWidth: 1,
										borderColor: yearbookHovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
										...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
									}}
								>
									<Ionicons name="book-outline" size={18} color="#fff" />
									<Text style={{ fontSize: 14, fontFamily: fonts.body, color: '#fff', fontWeight: '600' }}>
										View Yearbook Messages
									</Text>
								</TouchableOpacity>
							</Animated.View>

							{/* View Gifts with Hover */}
							<Animated.View style={{ flex: 1, transform: [{ scale: giftsButtonScale }] }}>
								<TouchableOpacity
									onPress={() => onScrollToWidget('giftTracking', 400)} // Scroll with offset
									onMouseEnter={() => {
										setGiftsHovered(true);
										handleButtonHover(giftsButtonScale, true);
									}}
									onMouseLeave={() => {
										setGiftsHovered(false);
										handleButtonHover(giftsButtonScale, false);
									}}
									activeOpacity={0.9}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 6,
										backgroundColor: giftsHovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
										paddingVertical: 12,
										paddingHorizontal: 14,
										borderRadius: 12,
										borderWidth: 1,
										borderColor: giftsHovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
										...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
									}}
								>
									<Ionicons name="gift-outline" size={18} color="#fff" />
									<Text style={{ fontSize: 14, fontFamily: fonts.body, color: '#fff', fontWeight: '600' }}>
										View Gifts
									</Text>
								</TouchableOpacity>
							</Animated.View>
						</View>
					</View>
				</View>

				{/* Right Card - Combined Progress & Actions */}
				<View style={{
					width: '100%',
					backgroundColor: colors.creamLight,
					borderRadius: 28,
					padding: isMobile ? 24 : 40,
					minHeight: isMobile ? 'auto' : 220,
					shadowColor: '#1a5c3a',
					shadowOffset: { width: 0, height: 4 },
					shadowOpacity: 0.12,
					shadowRadius: 12,
					elevation: 4,
					borderWidth: 1,
					borderColor: colors.border,
					position: 'relative'
				}}>
					{/* Decorative Bubbles */}
					<View style={{
						position: 'absolute',
						top: -20,
						left: 50,
						width: 60,
						height: 60,
						borderRadius: 30,
						backgroundColor: 'rgba(76, 149, 108, 0.06)'
					}} />
					<View style={{
						position: 'absolute',
						bottom: -15,
						right: 100,
						width: 50,
						height: 50,
						borderRadius: 25,
						backgroundColor: 'rgba(99, 102, 241, 0.05)'
					}} />
					
					{/* Two Progress Circles Row */}
					<View style={{
						flexDirection: isMobile ? 'column' : 'row',
						gap: isMobile ? 36 : 56,
						marginBottom: 36
					}}>
						{/* Thank You Progress */}
						<View style={{ flex: 1 }}>
							<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 24, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: '600' }}>
								Thank You Progress
							</Text>
							<View style={{ flexDirection: 'row', alignItems: 'center', gap: 28 }}>
								{/* Circular Progress */}
								<View style={{
									width: 130,
									height: 130,
									borderRadius: 65,
									backgroundColor: colors.creamDark,
									justifyContent: 'center',
									alignItems: 'center',
									position: 'relative'
								}}>
									<View style={{
										position: 'absolute',
										width: 130,
										height: 130,
										borderRadius: 65,
										borderWidth: 12,
										borderColor: colors.border
									}} />
									<View style={{
										position: 'absolute',
										width: 130,
										height: 130,
										borderRadius: 65,
										borderWidth: 12,
										borderColor: colors.primary,
										borderTopColor: completionPercent > 25 ? colors.primary : 'transparent',
										borderRightColor: completionPercent > 50 ? colors.primary : 'transparent',
										borderBottomColor: completionPercent > 75 ? colors.primary : 'transparent',
										borderLeftColor: completionPercent > 0 ? colors.primary : 'transparent',
										transform: [{ rotate: '-90deg' }]
									}} />
									<Text style={{ fontSize: 38, fontFamily: fonts.heading, color: colors.text }}>
										{completionPercent}%
									</Text>
								</View>
								<View style={{ flex: 1 }}>
									<View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 18 }}>
										<Text style={{ fontSize: 17, fontFamily: fonts.body, color: colors.textLight }}>Thanked</Text>
										<Text style={{ fontSize: 17, fontFamily: fonts.body, fontWeight: '700', color: colors.success }}>
											{thankYouSentCount}
										</Text>
									</View>
									<View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
										<Text style={{ fontSize: 17, fontFamily: fonts.body, color: colors.textLight }}>Needs Thank You</Text>
										<Text style={{ fontSize: 17, fontFamily: fonts.body, fontWeight: '700', color: needsThankYouCount > 0 ? colors.warning : colors.textMuted }}>
											{needsThankYouCount}
										</Text>
									</View>
								</View>
							</View>
						</View>


					</View>

					{/* Quick Action Button */}
					{/* {needsThankYouCount > 0 && (
						<View style={{
							backgroundColor: colors.primaryLight,
							borderRadius: 18,
							padding: 22,
							flexDirection: isMobile ? 'column' : 'row',
							alignItems: isMobile ? 'stretch' : 'center',
							justifyContent: 'space-between',
							gap: 18,
							borderWidth: 1,
							borderColor: colors.primary + '40'
						}}>
							<View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, flex: 1 }}>
								<View style={{
									backgroundColor: colors.primary,
									width: 50,
									height: 50,
									borderRadius: 14,
									justifyContent: 'center',
									alignItems: 'center'
								}}>
									<Text style={{ fontSize: 24 }}>✉️</Text>
								</View>
								<View>
									<Text style={{ fontSize: 19, fontFamily: fonts.heading, color: colors.primaryDark }}>
										{needsThankYouCount} thank you{needsThankYouCount !== 1 ? 's' : ''} pending
									</Text>
									<Text style={{ fontSize: 15, fontFamily: fonts.body, color: colors.textLight, marginTop: 4 }}>
										Send messages to your gift givers
									</Text>
								</View>
							</View>
							<TouchableOpacity
								onPress={() => {
									selectAllPending();
									setShowBulkMessageModal(true);
								}}
								onMouseEnter={() => setSendAllHovered(true)}
								onMouseLeave={() => setSendAllHovered(false)}
								activeOpacity={0.9}
								style={{
									backgroundColor: sendAllHovered ? '#3d7a58' : colors.primary,
									paddingVertical: 16,
									paddingHorizontal: 28,
									borderRadius: 14,
									alignItems: 'center',
									shadowColor: colors.primary,
									shadowOffset: { width: 0, height: sendAllHovered ? 6 : 3 },
									shadowOpacity: sendAllHovered ? 0.4 : 0.25,
									shadowRadius: sendAllHovered ? 10 : 6,
									transform: [{ scale: sendAllHovered ? 1.02 : 1 }],
									...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
								}}
							>
								<Text style={{ color: colors.white, fontFamily: fonts.body, fontWeight: '600', fontSize: 16 }}>
									Send All Thank You's
								</Text>
							</TouchableOpacity>
						</View>
					)} */}
				</View>
			</View>

			{/* Main Table Card */}
			<View style={{
				backgroundColor: colors.creamLight,
				borderRadius: 24,
				shadowColor: '#1a5c3a',
				shadowOffset: { width: 0, height: 4 },
				shadowOpacity: 0.12,
				shadowRadius: 12,
				elevation: 4,
				overflow: 'hidden',
				borderWidth: 1,
				borderColor: colors.border
			}}>
				{/* Table Header */}
				<View style={{
					padding: isMobile ? 20 : 28,
					borderBottomWidth: 1,
					borderBottomColor: colors.border,
					zIndex: 100
				}}>
				{/* Title and Buttons Row */}
				<View style={{ flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20, gap: isMobile ? 12 : 0 }}>
					<View>
						<Text style={{ fontSize: isMobile ? 22 : 26, fontFamily: fonts.heading, color: colors.text }}>
							Gifts and Contributions
						</Text>
						<Text style={{ fontSize: isMobile ? 13 : 15, fontFamily: fonts.body, color: colors.textMuted, marginTop: 6 }}>
							{filteredGifts.length} {filteredGifts.length === 1 ? 'gift' : 'gifts'} 
							{searchQuery && ` matching "${searchQuery}"`}
						</Text>
					</View>
					<View style={{ flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
							{/* Add Manual Contribution Button with Hover */}
							<Animated.View style={{ transform: [{ scale: goalButtonScale }] }}>
								<TouchableOpacity
									onPress={() => setShowManualContributionModal(true)}
									onMouseEnter={() => {
										setGoalHovered(true);
										handleButtonHover(goalButtonScale, true);
									}}
									onMouseLeave={() => {
										setGoalHovered(false);
										handleButtonHover(goalButtonScale, false);
									}}
									activeOpacity={0.9}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										backgroundColor: colors.creamDark,
										borderRadius: 12,
										paddingHorizontal: 14,
										paddingVertical: 10,
										borderWidth: 1,
										borderColor: colors.border,
										gap: 8
									}}
								>
									<Ionicons name="add-circle-outline" size={16} color={colors.textLight}  />
									<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textLight }}>
										Add Gift
									</Text>
								</TouchableOpacity>
							</Animated.View>
							
							{/* Export CSV Button */}
							{Platform.OS === 'web' && (
								<TouchableOpacity
									onPress={exportToCSV}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										backgroundColor: colors.creamDark,
										borderRadius: 12,
										paddingHorizontal: 14,
										paddingVertical: 10,
										borderWidth: 1,
										borderColor: colors.border,
										gap: 8
									}}
								>
									<Ionicons name="download-outline" size={16} color={colors.textLight} />
									<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textLight }}>
										Export
									</Text>
								</TouchableOpacity>
							)}

						{/* Search */}
						<View style={{
							flexDirection: 'row',
							alignItems: 'center',
							backgroundColor: colors.creamDark,
							borderRadius: 14,
							paddingHorizontal: 16,
							borderWidth: 1,
							borderColor: colors.border,
							minWidth: isMobile ? 140 : 220,
							flex: isMobile ? 1 : 0
						}}>
								<Text style={{ fontSize: 18, marginRight: 10 }}>🔍</Text>
								<TextInput
									style={{
										flex: 1,
										paddingVertical: 12,
										fontSize: 15,
										fontFamily: fonts.body,
										color: colors.text
									}}
									placeholder="Search gifts..."
									placeholderTextColor={colors.textMuted}
									value={searchQuery}
									onChangeText={setSearchQuery}
								/>
							</View>
						</View>
					</View>

	{/* Registry and Grad Fund Row */}
	{isMobile ? (
		<>
			{/* Mobile: stacked layout */}
			<View style={{ marginTop: 16 }}>
				<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: '600' }}>
					Registry Items ({totalItems} total)
				</Text>
				<View style={{ flexDirection: 'row', gap: 16 }}>
					<Text style={{ fontSize: 15, fontFamily: fonts.body, color: colors.success }}>
						{purchasedItems.length} Received
					</Text>
					<Text style={{ fontSize: 15, fontFamily: fonts.body, color: colors.warning }}>
						{Math.max(0, totalItems - purchasedItems.length)} Remaining
					</Text>
				</View>
			</View>
			<View style={{ marginTop: 24 }}>
				<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 28, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: '600' }}>
					Grad Fund Goal
				</Text>
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
					<View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
						<View style={{ width: '100%', height: 16, backgroundColor: colors.border, borderRadius: 8, overflow: 'visible', position: 'relative', marginTop: 8, flex: 1 }}>
							<View style={{ width: `${contributionProgress}%`, height: '100%', backgroundColor: colors.warning, borderRadius: 8 }} />
							<Text style={{ position: 'absolute', left: `${Math.max(contributionProgress - 10, 5)}%`, top: -22, fontSize: 12, fontFamily: fonts.body, color: colors.warning, fontWeight: '600' }}>
								${contributionTotal.toFixed(0)}
							</Text>
							<Text style={{ position: 'absolute', right: -5, top: -22, fontSize: 12, fontFamily: fonts.body, color: colors.textMuted, fontWeight: '600' }}>
								${contributionGoal.toFixed(0)}
							</Text>
						</View>
						<Text style={{ fontSize: 16, fontFamily: fonts.heading, color: colors.text }}>
							{contributionProgress}%
						</Text>
					</View>
				</View>
			</View>
		</>
	) : (
		<View style={{ flexDirection: 'row', gap: 50, justifyContent: 'space-between' }}>
			{/* Desktop: side-by-side layout */}
			<View style={{ flex: 0, minWidth: 140 }}>
				<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: '600' }}>
					Registry Items ({totalItems} total)
				</Text>
				<View>
					<Text style={{ fontSize: 17, fontFamily: fonts.body, color: colors.success, marginBottom: 8 }}>
						{purchasedItems.length} Received
					</Text>
					<Text style={{ fontSize: 17, fontFamily: fonts.body, color: colors.warning }}>
						{Math.max(0, totalItems - purchasedItems.length)} Remaining
					</Text>
				</View>
			</View>
			<View style={{ flex: 1, alignItems: 'flex-end' }}>
				<View>
					<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 24, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: '600' }}>
						Grad Fund Goal
					</Text>
					<View style={{ flexDirection: 'row', alignItems: 'center', gap: 28 }}>
						<View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
							<View style={{ width: 600, height: 16, backgroundColor: colors.border, borderRadius: 8, overflow: 'visible', position: 'relative', marginTop: 8 }}>
								<View style={{ width: `${contributionProgress}%`, height: '100%', backgroundColor: colors.warning, borderRadius: 8 }} />
								<Text style={{ position: 'absolute', left: `${Math.max(contributionProgress - 10, 5)}%`, top: -22, fontSize: 14, fontFamily: fonts.body, color: colors.warning, fontWeight: '600' }}>
									${contributionTotal.toFixed(0)}
								</Text>
								<Text style={{ position: 'absolute', right: -5, top: -22, fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, fontWeight: '600' }}>
									${contributionGoal.toFixed(0)}
								</Text>
							</View>
							<Text style={{ fontSize: 18, fontFamily: fonts.heading, color: colors.text }}>
								{contributionProgress}%
							</Text>
						</View>
					</View>
				</View>
			</View>
		</View>
	)}

					{/* Filter Pills Row with Sort */}
					<View style={{ marginTop: 20, zIndex: 50 }}>
						<View style={{ 
							flexDirection: 'row', 
							justifyContent: 'space-between', 
							alignItems: 'center',
							flexWrap: 'wrap',
							gap: 12
						}}>
							{/* Left side: Filter Pills */}
							<ScrollView 
								horizontal 
								showsHorizontalScrollIndicator={false}
								style={{ flex: 1 }}
							>
								<View style={{ flexDirection: 'row', gap: isMobile ? 8 : 10, alignItems: 'center' }}>
									{[
										{ key: 'all', label: 'All', mobileLabel: 'All', count: allGifts.length },
										{ key: 'new', label: 'New', mobileLabel: 'New', count: allGifts.filter(g => {
											const giftDate = new Date(g.date || 0);
											const weekAgo = new Date();
											weekAgo.setDate(weekAgo.getDate() - 7);
											return giftDate >= weekAgo;
										}).length },
										{ key: 'pending', label: 'Pending', mobileLabel: 'Pending', count: needsThankYouCount, highlight: true },
										{ key: 'ready', label: 'Ready', mobileLabel: 'Ready', count: allGifts.filter(g => {
											const status = getThankYouStatus(g);
											return status.sent;
										}).length },
										{ key: 'hidden', label: 'Hidden', mobileLabel: 'Hidden', count: anonymousCount }
									].map(filter => (
										<TouchableOpacity
											key={filter.key}
											onPress={() => setFilterType(filter.key)}
											style={{
												flexDirection: 'row',
												alignItems: 'center',
												paddingHorizontal: isMobile ? 12 : 16,
												paddingVertical: isMobile ? 8 : 10,
												borderRadius: 10,
												backgroundColor: filterType === filter.key 
													? (filter.highlight ? colors.warningLight : colors.primary)
													: colors.creamDark,
												borderWidth: 1,
												borderColor: filterType === filter.key 
													? (filter.highlight ? colors.warning : colors.primary)
													: colors.border,
												gap: 6
											}}
										>
											<Text style={{
												fontSize: isMobile ? 13 : 14,
												fontFamily: fonts.body,
												fontWeight: '500',
												color: filterType === filter.key 
													? (filter.highlight ? colors.warning : colors.white)
													: colors.textLight
											}}>
												{isMobile ? filter.mobileLabel : filter.label}
											</Text>
											<View style={{
												backgroundColor: filterType === filter.key 
													? (filter.highlight ? colors.warning : 'rgba(255,255,255,0.3)')
													: colors.border,
												paddingHorizontal: 6,
												paddingVertical: 2,
												borderRadius: 10
											}}>
												<Text style={{
													fontSize: 11,
													fontFamily: fonts.body,
													fontWeight: '600',
													color: filterType === filter.key 
														? colors.white
														: colors.textLight
												}}>
													{filter.count}
												</Text>
											</View>
										</TouchableOpacity>
									))}

								</View>
							</ScrollView>

							{/* Right side: Sort Dropdown */}
							<View style={{ position: 'relative', zIndex: 100 }}>
								<TouchableOpacity
									onPress={() => setShowSortDropdown(!showSortDropdown)}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										backgroundColor: colors.creamDark,
										borderRadius: 10,
										paddingHorizontal: 12,
										paddingVertical: 10,
										borderWidth: 1,
										borderColor: colors.border,
										gap: 6
									}}
								>
									<Ionicons name="swap-vertical" size={14} color={colors.textLight} />
									<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textLight }}>
										{sortOptions.find(s => s.key === sortBy)?.label.split(' ')[0] || 'Sort'}
									</Text>
									<Ionicons name={showSortDropdown ? "chevron-up" : "chevron-down"} size={12} color={colors.textMuted} />
								</TouchableOpacity>
								
								{showSortDropdown && (
									<View style={{
										position: 'absolute',
										top: '100%',
										right: 0,
										marginTop: 4,
										backgroundColor: colors.white,
										borderRadius: 12,
										borderWidth: 1,
										borderColor: colors.border,
										shadowColor: '#000',
										shadowOffset: { width: 0, height: 4 },
										shadowOpacity: 0.15,
										shadowRadius: 12,
										elevation: 1000,
										zIndex: 1000,
										minWidth: 180
									}}>
										{sortOptions.map((option, idx) => (
											<TouchableOpacity
												key={option.key}
												onPress={() => {
													setSortBy(option.key);
													setShowSortDropdown(false);
												}}
												style={{
													flexDirection: 'row',
													alignItems: 'center',
													paddingHorizontal: 14,
													paddingVertical: 10,
													gap: 8,
													backgroundColor: sortBy === option.key ? colors.primaryLight : 'transparent',
													borderTopWidth: idx > 0 ? 1 : 0,
													borderTopColor: colors.border,
													borderTopLeftRadius: idx === 0 ? 12 : 0,
													borderTopRightRadius: idx === 0 ? 12 : 0,
													borderBottomLeftRadius: idx === sortOptions.length - 1 ? 12 : 0,
													borderBottomRightRadius: idx === sortOptions.length - 1 ? 12 : 0
												}}
											>
												<Ionicons name={option.icon} size={14} color={sortBy === option.key ? colors.primary : colors.textMuted} />
												<Text style={{ 
													fontSize: 13, 
													fontFamily: fonts.body, 
													color: sortBy === option.key ? colors.primary : colors.text,
													fontWeight: sortBy === option.key ? '600' : '400',
													flex: 1
												}}>
													{option.label}
												</Text>
												{sortBy === option.key && (
													<Ionicons name="checkmark" size={14} color={colors.primary} />
												)}
											</TouchableOpacity>
										))}
									</View>
								)}
							</View>
						</View>

						{/* Select All Visible hint */}
						{selectedCount === 0 && (
							<View style={{
								marginTop: 14,
								flexDirection: 'row',
								alignItems: 'center',
								justifyContent: 'space-between'
							}}>
								<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted }}>
									{isMobile ? 'Tap' : 'Click'} rows to select{!isMobile && ' • Shift+click for range'}
								</Text>
								<TouchableOpacity
									onPress={selectAllVisible}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										paddingHorizontal: 12,
										paddingVertical: 6,
										borderRadius: 8,
										backgroundColor: colors.creamDark,
										borderWidth: 1,
										borderColor: colors.border,
										gap: 6
									}}
								>
									<Ionicons name="checkmark-done" size={14} color={colors.primary} />
									<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.primary, fontWeight: '500' }}>
										Select All
									</Text>
								</TouchableOpacity>
							</View>
						)}
					</View>
				</View>

				{/* Table Content */}
				{filteredGifts.length === 0 ? (
					<View style={{ padding: 70, alignItems: 'center', backgroundColor: colors.creamDark }}>
						{filterType === 'pending' && allGifts.length > 0 ? (
							<>
								<Text style={{ fontSize: 64, marginBottom: 20 }}>🎉</Text>
								<Text style={{ fontSize: 26, fontFamily: fonts.heading, color: colors.primary, marginBottom: 10 }}>
									All caught up!
								</Text>
								<Text style={{ fontSize: 16, fontFamily: fonts.body, color: colors.textMuted, textAlign: 'center', maxWidth: 320, lineHeight: 24 }}>
									You've sent thank you's to everyone. Great job showing your appreciation!
								</Text>
							</>
						) : searchQuery ? (
							<>
								<Text style={{ fontSize: 64, marginBottom: 20 }}>🔍</Text>
								<Text style={{ fontSize: 26, fontFamily: fonts.heading, color: colors.text, marginBottom: 10 }}>
									No matches found
								</Text>
								<Text style={{ fontSize: 16, fontFamily: fonts.body, color: colors.textMuted, textAlign: 'center', maxWidth: 320, lineHeight: 24 }}>
									Try adjusting your search or filters to find what you're looking for.
								</Text>
							</>
						) : (
							<>
								<Text style={{ fontSize: 64, marginBottom: 20 }}>🎁</Text>
								<Text style={{ fontSize: 26, fontFamily: fonts.heading, color: colors.text, marginBottom: 10 }}>
									No gifts yet
								</Text>
								<Text style={{ fontSize: 16, fontFamily: fonts.body, color: colors.textMuted, textAlign: 'center', maxWidth: 320, lineHeight: 24 }}>
									Share your registry with friends and family to start receiving gifts!
								</Text>
							</>
						)}
					</View>
				) : (
					<>
						{/* Desktop Info Header - Grouped view doesn't need traditional table headers */}
						{!isMobile && groupedGifts.length > 0 && (
							<View style={{
								paddingVertical: 12,
								paddingHorizontal: 24,
								backgroundColor: colors.creamDark,
								borderBottomWidth: 1,
								borderBottomColor: colors.border,
								flexDirection: 'row',
								justifyContent: 'space-between',
								alignItems: 'center'
							}}>
								<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted }}>
									{groupedGifts.length} gifter{groupedGifts.length !== 1 ? 's' : ''} • {filteredGifts.length} total gift{filteredGifts.length !== 1 ? 's' : ''}
								</Text>
							</View>
						)}

						{/* Table Rows - Grouped by Gifter */}
						<ScrollView style={{ maxHeight: isMobile ? 500 : 600 }}>
						{groupedGifts.map((gifterGroup, index) => {
							const isLastGroup = index === groupedGifts.length - 1;
							const getAvatarColors = () => {
								const colors_list = [
									{ bg: '#FEE2E2', text: '#991B1B' },
									{ bg: '#FEF3C7', text: '#92400E' },
									{ bg: '#DBEAFE', text: '#1E40AF' },
									{ bg: '#DCFCE7', text: '#166534' },
									{ bg: '#F3E8FF', text: '#6B21A8' }
								];
								return colors_list[index % colors_list.length];
							};
							const avatarColors = getAvatarColors();
							
							// Compute selection states for this gifterGroup
							const gifterGiftIds = new Set(gifterGroup.gifts.map(g => g.id));
							const selectedInGroup = Array.from(gifterGiftIds).filter(id => selectedIds.has(id));
							const someSelected = selectedInGroup.length > 0;
							const allSelected = selectedInGroup.length === gifterGroup.gifts.length;
							
							// Toggle selection for all gifts in this gifterGroup
							const toggleGifterSelection = () => {
								const newSelected = new Set(selectedIds);
								if (allSelected) {
									// Deselect all
									selectedInGroup.forEach(id => newSelected.delete(id));
								} else {
									// Select all
									gifterGroup.gifts.forEach(g => newSelected.add(g.id));
								}
								setSelectedIds(newSelected);
							};
							
							// Send thank you for this gifter
							const handleSendToGifter = () => {
								const giftsForThankYou = gifterGroup.gifts.filter(g => {
									const status = getThankYouStatus(g);
									return status.canSend;
								});
								
								if (giftsForThankYou.length === 0) return;
								
								// Set up ONE recipient for this gifter with all their gifts grouped together
								const recipient = {
									name: gifterGroup.name,
									email: gifterGroup.email || '',
									phone: gifterGroup.phone || '',
									gifts: giftsForThankYou,
									giftIds: giftsForThankYou.map(g => g.id),
									gifterKey: gifterGroup.gifterKey
								};
								
								setThankYouRecipients([recipient]);
								setSelectedTemplateIndex(0);
								setThankYouMessage(messageTemplates[0].message.replace(/\{\{name\}\}/g, gifterGroup.name?.split(' ')[0] || gifterGroup.name));
								setShowThankYouModal(true);
							};
							
							// Mobile Mini Layout
							if (isMobile) {
								return (
									<View
										key={gifterGroup.gifterKey}
										style={{
											marginHorizontal: 12,
											marginVertical: 6,
											backgroundColor: colors.white,
											borderRadius: 12,
											borderWidth: 1,
											borderColor: colors.border,
											overflow: 'hidden',
											shadowColor: '#1a5c3a',
											shadowOffset: { width: 0, height: 2 },
											shadowOpacity: 0.08,
											shadowRadius: 6,
											elevation: 2
										}}
									>
										{/* Mobile Header */}
										<TouchableOpacity
											activeOpacity={0.7}
											style={{
												flexDirection: 'row',
												alignItems: 'center',
												padding: 12,
												gap: 12,
												backgroundColor: colors.creamLight,
												borderBottomWidth: 1,
												borderBottomColor: colors.border
											}}
										>
											{gifterGroup.photoURL ? (
												<Image 
													source={{ uri: gifterGroup.photoURL }} 
													style={{ width: 44, height: 44, borderRadius: 22 }}
												/>
											) : (
												<View style={{
													width: 44,
													height: 44,
													borderRadius: 22,
													backgroundColor: avatarColors.bg,
													justifyContent: 'center',
													alignItems: 'center'
												}}>
													<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '600', color: avatarColors.text }}>
														{getInitials(gifterGroup.name)}
													</Text>
												</View>
											)}

											{/* Name and Stats */}
											<View style={{ flex: 1 }}>
												<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
													{gifterGroup.name}
												</Text>
												{(gifterGroup.email || gifterGroup.phone) && (
													<Text style={{ fontSize: 12, fontFamily: fonts.body, color: colors.textMuted, marginTop: 1 }}>
														{gifterGroup.email || gifterGroup.phone}
													</Text>
												)}
												{/* Gift details underneath name and email */}
												<View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 8 }}>
													<View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
														<Ionicons name="gift-outline" size={11} color={colors.textMuted} />
														<Text style={{ fontSize: 11, fontFamily: fonts.body, color: colors.textMuted }}>
															{gifterGroup.gifts.length} gift{gifterGroup.gifts.length !== 1 ? 's' : ''}
														</Text>
													</View>
													<View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
														<Ionicons name="calendar-outline" size={11} color={colors.textMuted} />
														<Text style={{ fontSize: 11, fontFamily: fonts.body, color: colors.textMuted }}>
															{formatDate(gifterGroup.latestDate)}
														</Text>
													</View>
												</View>
											</View>

										{/* Gift Amount Badge - only show dollar amount if group has cash contributions */}
										<View style={{
											alignItems: 'center'
										}}>
											{gifterGroup.gifts.some(g => g.type === 'contribution') ? (
												<Text style={{ fontSize: 18, fontFamily: fonts.body, fontWeight: '700', color: colors.primary }}>
													${gifterGroup.totalAmount.toFixed(0)}
												</Text>
											) : (
												<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted }}>
													{gifterGroup.gifts.length} {gifterGroup.gifts.length === 1 ? 'item' : 'items'}
												</Text>
											)}
												{/* Status indicators underneath the amount */}
												{gifterGroup.hasSent && !gifterGroup.hasUnsent && (
													<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 3 }}>
														<Ionicons name="checkmark-circle" size={11} color={colors.success} />
														<Text style={{ fontSize: 10, color: colors.success, fontWeight: '500' }}>Thanked</Text>
													</View>
												)}
												{gifterGroup.hasUnsent && (
													<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 3 }}>
														<View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: colors.warning }} />
														<Text style={{ fontSize: 10, color: colors.warning, fontWeight: '500' }}>Pending</Text>
													</View>
												)}
											</View>
										</TouchableOpacity>

											{/* Gift Items List */}
											<View style={{ padding: 12, gap: 8 }}>
												{gifterGroup.gifts.map((gift, giftIndex) => {
													const giftStatus = getThankYouStatus(gift);
													return (
														<View
															key={gift.id}
															style={{
																flexDirection: 'row',
																alignItems: 'center',
																padding: 10,
																backgroundColor: colors.creamLight,
																borderRadius: 10,
																gap: 10
															}}
														>
															{/* Type Badge */}
															<View style={{
																backgroundColor: gift.type === 'purchase' ? colors.secondaryLight : colors.warningLight,
																paddingHorizontal: 8,
																paddingVertical: 4,
																borderRadius: 6
															}}>
																<Text style={{ 
																	fontSize: 10, 
																	fontFamily: fonts.body,
																	fontWeight: '600', 
																	color: gift.type === 'purchase' ? colors.secondary : colors.warning 
																}}>
																	{gift.type === 'purchase' ? 'ITEM' : 'CASH'}
																</Text>
															</View>

															{/* Gift Description */}
															<Text style={{ flex: 1, fontSize: 13, fontFamily: fonts.body, color: colors.text }} numberOfLines={2}>
																{gift.giftDescription}
															</Text>

														{/* Amount - only show for cash contributions */}
														{gift.type === 'contribution' && gift.amount ? (
															<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
																{gift.amount}
															</Text>
														) : null}

															{/* Status indicator */}
															{giftStatus.sent && (
																<Ionicons name="checkmark-circle" size={16} color={colors.primary} />
															)}
														</View>
													);
												})}

												{/* Messages from gifter */}
												{gifterGroup.gifts.some(g => g.message) && (
													<View style={{
														marginTop: 4,
														padding: 10,
														backgroundColor: colors.creamDark,
														borderRadius: 8
													}}>
														{gifterGroup.gifts.filter(g => g.message).map((gift, i) => (
															<Text key={i} style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: i < gifterGroup.gifts.filter(g => g.message).length - 1 ? 6 : 0 }}>
																"{gift.message}"
															</Text>
														))}
													</View>
												)}
											</View>

											{/* Full Width Send Thank You Button with Hover */}
						{/* Mobile quick-send (only for non-anonymous with contact) */}
						{!gifterGroup.isAnonymous && (gifterGroup.email || gifterGroup.phone) && (
							<View style={{ marginTop: 10, marginBottom: 8 }}>
								{(() => {
									const cards = getCardsForGifter(gifterGroup);
									const last = cards.length ? cards.sort((a,b) => new Date(b.sentAt || b.sentDate) - new Date(a.sentAt || a.sentDate))[0] : null;
									if (false) {
										return (
											<View>
												<Text style={{ fontSize: 12, color: colors.textMuted }}>Sent {cards.length}</Text>
												
												<View style={{ marginTop: 6, flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
	
													<TouchableOpacity onPress={(e) => { e.stopPropagation(); openSentModal(gifterGroup); }}>
														<Text style={{ fontSize: 13, color: colors.textMuted }}>View Sent</Text>
													</TouchableOpacity>
												</View>
											</View>
										);
									}
									return (
										<View>
											<Text style={{ fontSize: 12, color: colors.textMuted }}>{cards.length ? `Sent ${cards.length}` : ''}</Text>
											<View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
												{(() => {
													const anim = getQuickSendAnim(gifterGroup.gifterKey);
													return (
														<Animated.View style={{ flex: 1, transform: [{ scale: anim.scale }], opacity: anim.opacity }}>
															<View style={{ position: 'relative' }} onStartShouldSetResponder={() => true} onTouchEnd={(e) => e.stopPropagation()}>
														<TextInput 
															value={quickMessages[gifterGroup.gifterKey] || last?.message || ''} 
															onChangeText={t => handleQuickMessageChange(gifterGroup.gifterKey, t)} 
															placeholder="Write a quick thank you..." 
															placeholderTextColor={colors.textMuted} 
															style={{ paddingHorizontal: 12, paddingVertical: 10, paddingRight: 50, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(76, 149, 108, 0.2)', backgroundColor: 'rgba(254, 253, 226, 0.95)', fontSize: 13, color: colors.textMuted, height: 80, minHeight: 48 }} 
															multiline={true}
															autoFocus={quickOpenKey === gifterGroup.gifterKey}
															onSubmitEditing={() => handleQuickSend(gifterGroup)} 
															returnKeyType="send" 
															onBlur={() => {
																if (!quickMessages[gifterGroup.gifterKey]?.trim()) {
																	setQuickMessages(prev => ({ ...prev, [gifterGroup.gifterKey]: '' }));
																}
															}}
														/>
														<TouchableOpacity 
															onPress={(e) => { e.stopPropagation(); handleQuickSend(gifterGroup, e); }} 
															disabled={quickSendingKey === gifterGroup.gifterKey} 
															style={{ 
																position: 'absolute', 
																right: 8, 
																top: '50%', 
																transform: [{ translateY: -12 }], 
																paddingHorizontal: 8, 
																paddingVertical: 6, 
																borderRadius: 20, 
																backgroundColor: 'transparent',
																justifyContent: 'center',
																alignItems: 'center'
															}}
														>
															<Ionicons name={quickSendingKey === gifterGroup.gifterKey ? 'hourglass' : 'paper-plane'} size={14} color={quickSendingKey === gifterGroup.gifterKey ? '#d1d5db' : colors.primary} />
														</TouchableOpacity>
													</View>
												</Animated.View>
												);
												})()}
											</View>
										</View>
									);
								})()}
							</View>
						)}
											{gifterGroup.canSend && (
												<TouchableOpacity
													onPress={handleSendToGifter}
													onMouseEnter={() => setSendThankYouHovered(gifterGroup.gifterKey)}
													onMouseLeave={() => setSendThankYouHovered(null)}
													activeOpacity={0.9}
													style={{
														flexDirection: 'row',
														alignItems: 'center',
														justifyContent: 'center',
														gap: 8,
														backgroundColor: gifterGroup.hasUnsent 
															? (sendThankYouHovered === gifterGroup.gifterKey ? '#3d7a58' : colors.primary)
															: (sendThankYouHovered === gifterGroup.gifterKey ? colors.secondary : colors.secondaryLight),
														paddingVertical: 14,
														borderTopWidth: 1,
														borderTopColor: gifterGroup.hasUnsent ? colors.primary : colors.border,
														...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
													}}
												>
													<Ionicons 
														name={gifterGroup.hasUnsent ? "mail" : "refresh"} 
														size={18} 
														color={gifterGroup.hasUnsent ? colors.white : (sendThankYouHovered === gifterGroup.gifterKey ? colors.white : colors.secondary)} 
													/>
													<Text style={{ 
														fontSize: 14, 
														fontFamily: fonts.body,
														fontWeight: '600', 
														color: gifterGroup.hasUnsent ? colors.white : (sendThankYouHovered === gifterGroup.gifterKey ? colors.white : colors.secondary)
													}}>
														{gifterGroup.hasUnsent ? 'Send Thank You' : 'Resend Thank You'}
													</Text>
												</TouchableOpacity>
											)}
										</View>
									);
								}

								// Desktop Grouped Card Layout
								return (
									<View
										key={gifterGroup.gifterKey}
										style={{
											margin: 16,
											marginBottom: isLastGroup ? 16 : 0,
											backgroundColor: someSelected ? colors.primaryLight : colors.white,
											borderRadius: 16,
											borderWidth: 1,
											borderColor: someSelected ? colors.primary : colors.border,
											overflow: 'hidden'
										}}
									>
										{/* Gifter Header Row */}
										<TouchableOpacity
											onPress={toggleGifterSelection}
											onMouseEnter={() => setHoveredRow(gifterGroup.gifterKey)}
											onMouseLeave={() => setHoveredRow(null)}
											activeOpacity={0.7}
											style={{
												flexDirection: 'row',
												alignItems: 'center',
												padding: 18,
												gap: 16,
												backgroundColor: someSelected ? 'rgba(76, 149, 108, 0.08)' : hoveredRow === gifterGroup.gifterKey ? 'rgba(76, 149, 108, 0.04)' : colors.creamLight,
												borderBottomWidth: 1,
												borderBottomColor: colors.border
											}}
										>
											{/* Checkbox */}
											{gifterGroup.canSend && (
												<View style={{
													width: 24,
													height: 24,
													borderRadius: 7,
													borderWidth: 2,
													borderColor: allSelected ? colors.primary : someSelected ? colors.primary : colors.textMuted,
													backgroundColor: allSelected ? colors.primary : colors.white,
													justifyContent: 'center',
													alignItems: 'center'
												}}>
													{allSelected && <Text style={{ color: colors.white, fontSize: 14 }}>✓</Text>}
													{someSelected && !allSelected && <View style={{ width: 10, height: 10, backgroundColor: colors.primary, borderRadius: 2 }} />}
												</View>
											)}

											{/* Avatar */}
											{gifterGroup.photoURL ? (
												<Image 
													source={{ uri: gifterGroup.photoURL }} 
													style={{ width: 50, height: 50, borderRadius: 25 }}
												/>
											) : (
												<View style={{
													width: 50,
													height: 50,
													borderRadius: 25,
													backgroundColor: avatarColors.bg,
													justifyContent: 'center',
													alignItems: 'center'
												}}>
													<Text style={{ fontSize: 17, fontFamily: fonts.body, fontWeight: '600', color: avatarColors.text }}>
														{getInitials(gifterGroup.name)}
													</Text>
												</View>
											)}

											{/* Name and Contact Info */}
											<View style={{ flex: 1 }}>
												<Text style={{ fontSize: 17, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
													{gifterGroup.name}
												</Text>
												{(gifterGroup.email || gifterGroup.phone) && (
													<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, marginTop: 2 }}>
														{gifterGroup.email || gifterGroup.phone}
													</Text>
												)}
												{/* Gift details underneath name and email */}
												<View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 6, gap: 12 }}>
													<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
														<Ionicons name="gift-outline" size={12} color={colors.textMuted} />
														<Text style={{ fontSize: 12, fontFamily: fonts.body, color: colors.textMuted }}>
															{gifterGroup.gifts.length} gift{gifterGroup.gifts.length !== 1 ? 's' : ''}
														</Text>
													</View>
													<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
														<Ionicons name="calendar-outline" size={12} color={colors.textMuted} />
														<Text style={{ fontSize: 12, fontFamily: fonts.body, color: colors.textMuted }}>
															{formatDate(gifterGroup.latestDate)}
														</Text>
													</View>
												</View>
											</View>

											{/* Send Thank You Button with Hover */}
								{/* Desktop quick-send (only for non-anonymous with contact) */}
								{!gifterGroup.isAnonymous && (gifterGroup.email || gifterGroup.phone) && (
									<View style={{ marginRight: 12, alignItems: 'flex-end' }}>
										{(() => {
										const cards = getCardsForGifter(gifterGroup);
										const last = cards.length ? cards.sort((a,b) => new Date(b.sentAt || b.sentDate) - new Date(a.sentAt || a.sentDate))[0] : null;
										if (last && quickOpenKey !== gifterGroup.gifterKey) {
											return (
											<View style={{ alignItems: 'flex-end' }}>
												<Text style={{ fontSize: 12, color: colors.textMuted }}>Sent {cards.length}</Text>
												
											<View style={{ marginTop: 6, flexDirection: 'row', gap: 8 }}>
												<TouchableOpacity onPress={(e) => { e.stopPropagation(); setQuickOpenKey(gifterGroup.gifterKey); handleQuickMessageChange(gifterGroup.gifterKey, last.message || (last.personalizedMessages && last.personalizedMessages[0] && last.personalizedMessages[0].message) || ''); }}>
													<Text style={{ fontSize: 13, color: colors.primary }}>Send Again</Text>
												</TouchableOpacity>
												<TouchableOpacity onPress={(e) => { e.stopPropagation(); openSentModal(gifterGroup); }}>
													<Text style={{ fontSize: 13, color: colors.textMuted }}>View Sent</Text>
												</TouchableOpacity>
											</View>
											</View>
										);
										}
										return (
											<View style={{ width: 220 }}>
												<View style={{ flexDirection: 'row', gap: 8 }}>
													{(() => {
														const anim = getQuickSendAnim(gifterGroup.gifterKey);
														return (
															<Animated.View style={{ flex: 1, transform: [{ scale: anim.scale }], opacity: anim.opacity }}>
																<View style={{ position: 'relative' }} onStartShouldSetResponder={() => true} onTouchEnd={(e) => e.stopPropagation()}>
															<TextInput 
																value={quickMessages[gifterGroup.gifterKey] || last?.message || ''} 
																onChangeText={t => handleQuickMessageChange(gifterGroup.gifterKey, t)} 
																placeholder="Write a quick thank you..." 
																placeholderTextColor={colors.textMuted} 
																style={{ paddingHorizontal: 12, paddingVertical: 10, paddingRight: 50, borderRadius: 12, borderWidth: 1, borderColor: 'rgba(76, 149, 108, 0.2)', backgroundColor: 'rgba(254, 253, 226, 0.95)', fontSize: 13, color: colors.textMuted, maxHeight: 42, }} 
																multiline={true}
																autoFocus={quickOpenKey === gifterGroup.gifterKey}
																onSubmitEditing={() => handleQuickSend(gifterGroup)} 
																returnKeyType="send" 
																onBlur={() => {
																	if (!quickMessages[gifterGroup.gifterKey]?.trim()) {
																		setQuickOpenKey(null);
																		setQuickMessages(prev => ({ ...prev, [gifterGroup.gifterKey]: '' }));
																	}
																}}
															/>
															<TouchableOpacity 
																onPress={(e) => { e.stopPropagation(); handleQuickSend(gifterGroup, e); }} 
																disabled={quickSendingKey === gifterGroup.gifterKey} 
																style={{ 
																	position: 'absolute', 
																	right: 8, 
																	top: '50%', 
																	transform: [{ translateY: -12 }], 
																	paddingHorizontal: 8, 
																	paddingVertical: 6, 
																	borderRadius: 20, 
																	backgroundColor: 'transparent',
																	justifyContent: 'center',
																	alignItems: 'center'
																}}
															>
																<Ionicons name={quickSendingKey === gifterGroup.gifterKey ? 'hourglass' : 'paper-plane'} size={14} color={quickSendingKey === gifterGroup.gifterKey ? '#d1d5db' : colors.primary} />
															</TouchableOpacity>
														</View>
													</Animated.View>
													);
													})()}
												</View>
											</View>
										);
										})()}
									</View>
								)}
											{gifterGroup.canSend && (
												<TouchableOpacity
													onPress={(e) => {
														e.stopPropagation();
														handleSendToGifter();
													}}
													onMouseEnter={() => setSendThankYouHovered('desktop-' + gifterGroup.gifterKey)}
													onMouseLeave={() => setSendThankYouHovered(null)}
													activeOpacity={0.9}
													style={{
														backgroundColor: gifterGroup.hasUnsent 
															? (sendThankYouHovered === 'desktop-' + gifterGroup.gifterKey ? '#3d7a58' : colors.primary)
															: (sendThankYouHovered === 'desktop-' + gifterGroup.gifterKey ? colors.secondary : colors.secondaryLight),
														paddingHorizontal: 16,
														paddingVertical: 10,
														borderRadius: 10,
														flexDirection: 'row',
														alignItems: 'center',
														gap: 6,
														shadowColor: gifterGroup.hasUnsent ? colors.primary : colors.secondary,
														shadowOffset: { width: 0, height: sendThankYouHovered === 'desktop-' + gifterGroup.gifterKey ? 4 : 2 },
														shadowOpacity: sendThankYouHovered === 'desktop-' + gifterGroup.gifterKey ? 0.3 : 0.15,
														shadowRadius: sendThankYouHovered === 'desktop-' + gifterGroup.gifterKey ? 8 : 4,
														...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
													}}
												>
													<Ionicons 
														name={gifterGroup.hasUnsent ? "mail" : "refresh"} 
														size={16} 
														color={gifterGroup.hasUnsent ? colors.white : (sendThankYouHovered === 'desktop-' + gifterGroup.gifterKey ? colors.white : colors.secondary)} 
													/>
													<Text style={{ 
														fontSize: 13, 
														fontFamily: fonts.body,
														fontWeight: '600', 
														color: gifterGroup.hasUnsent ? colors.white : (sendThankYouHovered === 'desktop-' + gifterGroup.gifterKey ? colors.white : colors.secondary)
													}}>
														{gifterGroup.hasUnsent ? 'Send Thank You' : 'Resend'}
													</Text>
												</TouchableOpacity>
											)}
										{/* Gift Amount Badge - only show dollar amount if group has cash contributions */}
										<View style={{
											alignItems: 'center',
											marginLeft: 12
										}}>
											{gifterGroup.gifts.some(g => g.type === 'contribution') ? (
												<Text style={{ fontSize: 20, fontFamily: fonts.body, fontWeight: '700', color: colors.primary }}>
													${gifterGroup.totalAmount.toFixed(0)}
												</Text>
											) : (
												<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted }}>
													{gifterGroup.gifts.length} {gifterGroup.gifts.length === 1 ? 'item' : 'items'}
												</Text>
											)}
												{/* Status indicators underneath the amount */}
												<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 }}>
													{gifterGroup.hasSent && !gifterGroup.hasUnsent && (
														<View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
															<Ionicons name="checkmark-circle" size={12} color={colors.success} />
															<Text style={{ fontSize: 11, color: colors.success, fontWeight: '500' }}>Thanked</Text>
														</View>
													)}
													{gifterGroup.hasUnsent && (
														<View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
															<View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.warning }} />
															<Text style={{ fontSize: 11, color: colors.warning, fontWeight: '500' }}>Pending</Text>
														</View>
													)}
												</View>
											</View>
										</TouchableOpacity>

										{/* Gift Items List */}
										<View style={{ padding: 16 }}>
											{gifterGroup.gifts.map((gift, giftIndex) => {
												const giftStatus = getThankYouStatus(gift);
												const giftNote = giftNotes[gift.id] || '';
												return (
													<View
														key={gift.id}
														style={{
															flexDirection: 'row',
															alignItems: 'center',
															padding: 14,
															backgroundColor: colors.creamLight,
															borderRadius: 12,
															marginBottom: giftIndex < gifterGroup.gifts.length - 1 ? 10 : 0,
															gap: 14
														}}
													>
														{/* Type Badge */}
														<View style={{
															backgroundColor: gift.type === 'purchase' ? colors.secondaryLight : colors.warningLight,
															paddingHorizontal: 10,
															paddingVertical: 5,
															borderRadius: 6
														}}>
															<Text style={{ 
																fontSize: 11, 
																fontFamily: fonts.body,
																fontWeight: '700', 
																color: gift.type === 'purchase' ? colors.secondary : colors.warning 
															}}>
																{gift.type === 'purchase' ? 'ITEM' : 'CASH'}
															</Text>
														</View>

														{/* Gift Description and Date */}
														<View style={{ flex: 1 }}>
															<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.text }} numberOfLines={1}>
																{gift.giftDescription}
															</Text>
															{gift.message && (
																<Text style={{ fontSize: 12, fontFamily: fonts.body, color: colors.textMuted, fontStyle: 'italic', marginTop: 4 }} numberOfLines={1}>
																	"{gift.message}"
																</Text>
															)}
															{/* Date underneath description */}
															<Text style={{ fontSize: 12, fontFamily: fonts.body, color: colors.textMuted, marginTop: 4 }}>
																{formatDate(gift.date)}
															</Text>
														</View>

													{/* Amount - only show for cash contributions */}
													{gift.type === 'contribution' && gift.amount ? (
														<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '600', color: colors.text, minWidth: 60, textAlign: 'right' }}>
															{gift.amount}
														</Text>
													) : (
														<View style={{ minWidth: 60 }} />
													)}

														{/* Status Icon */}
														{giftStatus.sent ? (
															<Ionicons name="checkmark-circle" size={18} color={colors.primary} />
														) : giftStatus.canSend ? (
															<View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: colors.warning }} />
														) : null}

														{/* Note Button */}
														<TouchableOpacity
															onPress={(e) => {
																e.stopPropagation();
																setEditingNoteId(editingNoteId === gift.id ? null : gift.id);
																setNoteInput(giftNote);
															}}
															style={{
																padding: 6,
																borderRadius: 6,
																backgroundColor: giftNote ? colors.warningLight : 'transparent'
															}}
														>
															<Ionicons 
																name={giftNote ? "document-text" : "document-text-outline"} 
																size={16} 
																color={giftNote ? colors.warning : colors.textMuted} 
															/>
														</TouchableOpacity>
													</View>
												);
											})}
										</View>
									</View>
								);
							})}
						</ScrollView>
					</>
				)}

				{/* Bottom Selection Bar - appears when items selected */}
				{selectedCount > 0 && (
					<View style={{
						backgroundColor: colors.primaryLight,
						padding: 16,
						borderTopWidth: 1,
						borderTopColor: colors.primary + '30'
					}}>
						<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
							{/* Left side - Avatar stack and info */}
								<View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
									{/* Avatar stack */}
									<View style={{ flexDirection: 'row' }}>
										{uniqueRecipients.slice(0, 3).map((recipient, i) => {
											const ac = getAvatarColor(recipient.name);
											return (
												<View key={i} style={{ marginLeft: i > 0 ? -10 : 0, zIndex: 3 - i }}>
													{recipient.photoURL ? (
														<Image 
															source={{ uri: recipient.photoURL }} 
															style={{ 
																width: 36, 
																height: 36, 
																borderRadius: 18,
																borderWidth: 2,
																borderColor: colors.primaryLight
															}}
														/>
													) : (
														<View style={{
															width: 36,
															height: 36,
															borderRadius: 18,
															backgroundColor: ac.bg,
															justifyContent: 'center',
															alignItems: 'center',
															borderWidth: 2,
															borderColor: colors.primaryLight
														}}>
															<Text style={{ fontSize: 12, fontFamily: fonts.body, fontWeight: '600', color: ac.text }}>
																{getInitials(recipient.name)}
															</Text>
														</View>
													)}
												</View>
											);
										})}
										{uniqueRecipients.length > 3 && (
											<View style={{ 
												marginLeft: -10, 
												width: 36, 
												height: 36, 
												borderRadius: 18, 
												backgroundColor: colors.primary,
												justifyContent: 'center',
												alignItems: 'center',
												borderWidth: 2,
												borderColor: colors.primaryLight
											}}>
												<Text style={{ fontSize: 11, fontFamily: fonts.body, fontWeight: '600', color: colors.white }}>
													+{uniqueRecipients.length - 3}
												</Text>
											</View>
										)}
									</View>
									
									{/* Info */}
									<View style={{ flex: 1 }}>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.text }} numberOfLines={1}>
											{uniqueRecipients.length === 1 
												? uniqueRecipients[0].name 
												: `${uniqueRecipients.length} recipients`
											}
										</Text>
										<Text style={{ fontSize: 12, fontFamily: fonts.body, color: colors.textMuted }}>
											{selectedCount} gift{selectedCount !== 1 ? 's' : ''} selected
										</Text>
									</View>
								</View>

								{/* Right side - Actions */}
								<View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
									<TouchableOpacity onPress={clearSelection}>
										<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, textDecorationLine: 'underline' }}>
											Clear
										</Text>
									</TouchableOpacity>
									<TouchableOpacity
										onPress={() => openThankYouModal()}
										onMouseEnter={() => setBottomSendHovered(true)}
										onMouseLeave={() => setBottomSendHovered(false)}
										activeOpacity={0.9}
										style={{
											flexDirection: 'row',
											alignItems: 'center',
											backgroundColor: bottomSendHovered ? '#3d7a58' : colors.primary,
											paddingHorizontal: 16,
											paddingVertical: 12,
											borderRadius: 10,
											gap: 8,
											shadowColor: colors.primary,
											shadowOffset: { width: 0, height: bottomSendHovered ? 6 : 3 },
											shadowOpacity: bottomSendHovered ? 0.4 : 0.25,
											shadowRadius: bottomSendHovered ? 10 : 6,
											elevation: bottomSendHovered ? 6 : 4,
											transform: [{ scale: bottomSendHovered ? 1.02 : 1 }],
											...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
										}}
									>
										<Ionicons name="send" size={16} color={colors.white} />
										<Text style={{ color: colors.white, fontFamily: fonts.body, fontWeight: '600', fontSize: 14 }}>
											Send Thank You
										</Text>
									</TouchableOpacity>
								</View>
							</View>
						</View>
					)}
			</View>

			{/* Gift Note Modal */}
			<Modal
				visible={editingNoteId !== null}
				transparent
				animationType="fade"
				onRequestClose={() => { setEditingNoteId(null); setNoteInput(''); }}
			>
				<View style={{
					flex: 1,
					backgroundColor: 'rgba(0,0,0,0.6)',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 20
				}}>
					<View style={{
						backgroundColor: colors.cream,
						borderRadius: 24,
						padding: isMobile ? 20 : 24,
						width: '100%',
						maxWidth: 520,
						shadowColor: '#000',
						shadowOffset: { width: 0, height: 10 },
						shadowOpacity: 0.12,
						shadowRadius: 20,
						borderWidth: 1,
						borderColor: colors.border
					}}>
						<View style={{ marginBottom: 12 }}>
							{/* Top row: Title and close icon */}
							<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
								<Text numberOfLines={1} ellipsizeMode='tail' style={{ fontSize: 20, fontFamily: fonts.heading, color: colors.text, flex: 1, marginRight: 8 }}>Gift Note</Text>
								<TouchableOpacity onPress={() => { setEditingNoteId(null); setNoteInput(''); }}>
									<Ionicons name="close" size={20} color={colors.textMuted} />
								</TouchableOpacity>
							</View>

							{/* Metadata row: sender & item title (left), amount (right) */}
							<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
								<View style={{ flex: 1, marginRight: 12 }}>
									<Text numberOfLines={1} style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '700', color: colors.text }}>{currentNoteGift?.name || ''}</Text>
									<Text numberOfLines={2} ellipsizeMode='tail' style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, marginTop: 2, ...(Platform.OS === 'web' ? { wordBreak: 'break-word', overflowWrap: 'break-word' } : {}) }}>{currentNoteGift?.giftDescription || ''}</Text>
								</View>
								<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '700', color: colors.primary, minWidth: 80, textAlign: 'right', flexShrink: 0 }}>{currentNoteGift?.amount || ''}</Text>
							</View>
						</View>
						<TextInput
							value={noteInput}
							onChangeText={setNoteInput}
							placeholder="Add notes about this gift"
							multiline
							style={{ minHeight: 100, borderRadius: 10, borderWidth: 1, borderColor: colors.border, padding: 12, fontFamily: fonts.body, fontSize: 14, color: colors.text, backgroundColor: colors.white }}
						/>

						<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
							<TouchableOpacity onPress={() => { setEditingNoteId(null); setNoteInput(''); }} style={{ paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10, backgroundColor: colors.creamDark, borderWidth: 1, borderColor: colors.border }}>
								<Text style={{ fontFamily: fonts.body, color: colors.textMuted }}>Cancel</Text>
							</TouchableOpacity>
							<TouchableOpacity onPress={() => handleSaveNote(editingNoteId)} style={{ paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10, backgroundColor: colors.primary }}>
								<Text style={{ fontFamily: fonts.body, color: colors.white, fontWeight: '600' }}>Save</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</Modal>

			{/* Unified Thank You Modal */}
			<Modal
				visible={showThankYouModal}
				transparent
				animationType="fade"
				onRequestClose={() => setShowThankYouModal(false)}
			>
				<View style={{
					flex: 1,
					backgroundColor: 'rgba(0,0,0,0.6)',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 20
				}}>
					<ScrollView 
						style={{ maxHeight: '90%', width: '100%' }}
						contentContainerStyle={{ alignItems: 'center' }}
						showsVerticalScrollIndicator={false}
					>
						<View style={{
							backgroundColor: colors.cream,
							borderRadius: 24,
							padding: isMobile ? 20 : 28,
							width: '100%',
							maxWidth: 520,
							shadowColor: '#1a5c3a',
							shadowOffset: { width: 0, height: 10 },
							shadowOpacity: 0.2,
							shadowRadius: 30,
							borderWidth: 1,
							borderColor: colors.border
						}}>
							{/* Modal Header */}
							<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
								<View>
									<Text style={{ fontSize: isMobile ? 22 : 26, fontFamily: fonts.heading, color: colors.text }}>
										Send Thank You
									</Text>
									<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginTop: 4 }}>
										{thankYouRecipients.length} recipient{thankYouRecipients.length !== 1 ? 's' : ''} • {thankYouRecipients.reduce((sum, r) => sum + (r.gifts?.length || 1), 0)} gift{thankYouRecipients.reduce((sum, r) => sum + (r.gifts?.length || 1), 0) !== 1 ? 's' : ''}
									</Text>
								</View>
								<TouchableOpacity
									onPress={() => {
										setShowThankYouModal(false);
										setThankYouRecipients([]);
										setThankYouMessage('');
									}}
									style={{
										width: 40,
										height: 40,
										borderRadius: 20,
										backgroundColor: colors.creamDark,
										justifyContent: 'center',
										alignItems: 'center',
										borderWidth: 1,
										borderColor: colors.border
									}}
								>
									<Ionicons name="close" size={20} color={colors.textMuted} />
								</TouchableOpacity>
							</View>

							{/* Recipients */}
							<Text style={{ fontSize: 12, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
								Recipients
							</Text>
							<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
								{thankYouRecipients.slice(0, 6).map((recipient, i) => {
									const ac = getAvatarColor(recipient.name);
									return (
										<View key={i} style={{
											flexDirection: 'row',
											alignItems: 'center',
											backgroundColor: colors.creamDark,
											paddingLeft: 4,
											paddingRight: 12,
											paddingVertical: 6,
											borderRadius: 24,
											gap: 8,
											borderWidth: 1,
											borderColor: colors.border
										}}>
											{recipient.photoURL ? (
												<Image 
													source={{ uri: recipient.photoURL }} 
													style={{ width: 28, height: 28, borderRadius: 14 }}
												/>
											) : (
												<View style={{
													width: 28,
													height: 28,
													borderRadius: 14,
													backgroundColor: ac.bg,
													justifyContent: 'center',
													alignItems: 'center'
												}}>
													<Text style={{ fontSize: 11, fontFamily: fonts.body, fontWeight: '600', color: ac.text }}>
														{getInitials(recipient.name)}
													</Text>
												</View>
											)}
											<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.text }}>
												{recipient.name?.split(' ')[0]}
												{(recipient.gifts?.length || 0) > 1 && (
													<Text style={{ color: colors.textMuted }}> ({recipient.gifts.length})</Text>
												)}
											</Text>
										</View>
									);
								})}
								{thankYouRecipients.length > 6 && (
									<View style={{
										backgroundColor: colors.creamDark,
										paddingHorizontal: 14,
										paddingVertical: 8,
										borderRadius: 24,
										borderWidth: 1,
										borderColor: colors.border
									}}>
										<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted }}>
											+{thankYouRecipients.length - 6} more
										</Text>
									</View>
								)}
							</View>

							{/* Template Selection */}
							<Text style={{ fontSize: 12, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
								Choose a Template
							</Text>
							<View style={{ gap: 10, marginBottom: 20 }}>
								{messageTemplates.map((template, index) => (
									<TouchableOpacity
										key={index}
										onPress={() => selectTemplate(index)}
										style={{
											padding: 14,
											borderRadius: 12,
											backgroundColor: selectedTemplateIndex === index ? colors.primaryLight : colors.white,
											borderWidth: 2,
											borderColor: selectedTemplateIndex === index ? colors.primary : colors.border,
											flexDirection: 'row',
											alignItems: 'center',
											gap: 12
										}}
									>
										<View style={{ flex: 1 }}>
											<Text style={{ 
												fontSize: 15, 
												fontFamily: fonts.body, 
												fontWeight: '600', 
												color: selectedTemplateIndex === index ? colors.primary : colors.text 
											}}>
												{template.name}
											</Text>
											<Text style={{ 
												fontSize: 13, 
												fontFamily: fonts.body, 
												color: colors.textMuted,
												marginTop: 4
											}} numberOfLines={1}>
												{template.message.replace(/\{\{name\}\}/g, thankYouRecipients[0]?.name?.split(' ')[0] || 'Friend').split('\n')[0]}...
											</Text>
										</View>
										{selectedTemplateIndex === index && (
											<View style={{
												width: 24,
												height: 24,
												borderRadius: 12,
												backgroundColor: colors.primary,
												justifyContent: 'center',
												alignItems: 'center'
											}}>
												<Ionicons name="checkmark" size={16} color={colors.white} />
											</View>
										)}
									</TouchableOpacity>
								))}
							</View>

							{/* Editable Message */}
							<Text style={{ fontSize: 12, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
								Your Message {thankYouRecipients.length > 1 && <Text style={{ fontWeight: '400', textTransform: 'none' }}>(names auto-personalized)</Text>}
							</Text>
							<TextInput
								style={{
									backgroundColor: colors.white,
									borderWidth: 1,
									borderColor: colors.border,
									borderRadius: 14,
									padding: 16,
									fontSize: 15,
									fontFamily: fonts.body,
									minHeight: 140,
									textAlignVertical: 'top',
									color: colors.text,
									lineHeight: 22
								}}
								placeholder="Write your thank you message..."
								placeholderTextColor={colors.textMuted}
								value={thankYouMessage}
								onChangeText={setThankYouMessage}
								multiline
							/>
							
							{/* Personalization hint for multiple recipients */}
							{thankYouRecipients.length > 1 && (
								<View style={{
									flexDirection: 'row',
									alignItems: 'center',
									gap: 8,
									marginTop: 10,
									padding: 12,
									backgroundColor: colors.primaryLight,
									borderRadius: 10,
									borderWidth: 1,
									borderColor: colors.primary + '30'
								}}>
									<Ionicons name="sparkles" size={16} color={colors.primary} />
									<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.primaryDark, flex: 1 }}>
										Each recipient will receive a personalized message with their name
									</Text>
								</View>
							)}

							{/* Delivery Info */}
							<View style={{
								flexDirection: 'row',
								gap: 16,
								marginTop: 16,
								padding: 14,
								backgroundColor: colors.creamDark,
								borderRadius: 12,
								borderWidth: 1,
								borderColor: colors.border
							}}>
								<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
									<Ionicons name="mail-outline" size={18} color={colors.textLight} />
									<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textLight }}>
										{thankYouRecipients.filter(r => r.email).length} email{thankYouRecipients.filter(r => r.email).length !== 1 ? 's' : ''}
									</Text>
								</View>
								<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
									<Ionicons name="chatbubble-outline" size={18} color={colors.textLight} />
									<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textLight }}>
										{thankYouRecipients.filter(r => r.phone && !r.email).length} SMS
									</Text>
								</View>
							</View>

							{/* Actions */}
							<View style={{ flexDirection: 'row', gap: 12, marginTop: 24 }}>
								<TouchableOpacity
									onPress={() => {
										setShowThankYouModal(false);
										setThankYouRecipients([]);
										setThankYouMessage('');
									}}
									onMouseEnter={() => setModalCancelHovered(true)}
									onMouseLeave={() => setModalCancelHovered(false)}
									activeOpacity={0.9}
									style={{
										flex: 1,
										paddingVertical: 16,
										borderRadius: 14,
										backgroundColor: modalCancelHovered ? colors.creamDarker || '#e8e4d4' : colors.creamDark,
										alignItems: 'center',
										borderWidth: 1,
										borderColor: modalCancelHovered ? colors.textMuted : colors.border,
										transform: [{ scale: modalCancelHovered ? 1.02 : 1 }],
										...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
									}}
								>
									<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
										Cancel
									</Text>
								</TouchableOpacity>
								
								<TouchableOpacity
									onPress={handleUnifiedSend}
									onMouseEnter={() => thankYouMessage.trim() && !isSending && setModalSendHovered(true)}
									onMouseLeave={() => setModalSendHovered(false)}
									disabled={!thankYouMessage.trim() || isSending}
									activeOpacity={0.9}
									style={{
										flex: 2,
										paddingVertical: 16,
										borderRadius: 14,
										backgroundColor: !thankYouMessage.trim() ? colors.creamDark : modalSendHovered ? '#3d7a58' : colors.primary,
										alignItems: 'center',
										flexDirection: 'row',
										justifyContent: 'center',
										gap: 8,
										borderWidth: thankYouMessage.trim() ? 0 : 1,
										borderColor: colors.border,
										shadowColor: colors.primary,
										shadowOffset: { width: 0, height: modalSendHovered && thankYouMessage.trim() ? 6 : 3 },
										shadowOpacity: thankYouMessage.trim() ? (modalSendHovered ? 0.4 : 0.25) : 0,
										shadowRadius: modalSendHovered ? 10 : 6,
										transform: [{ scale: modalSendHovered && thankYouMessage.trim() ? 1.02 : 1 }],
										...(Platform.OS === 'web' ? { cursor: thankYouMessage.trim() && !isSending ? 'pointer' : 'not-allowed', transition: 'all 0.2s ease' } : {})
									}}
								>
									{isSending ? (
										<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.white }}>
											Sending...
										</Text>
									) : (
										<>
											<Ionicons name="send" size={18} color={thankYouMessage.trim() ? colors.white : colors.textMuted} />
											<Text style={{ 
												fontSize: 16, 
												fontFamily: fonts.body,
												fontWeight: '600', 
												color: thankYouMessage.trim() ? colors.white : colors.textMuted 
											}}>
												Send to {thankYouRecipients.length} Recipient{thankYouRecipients.length !== 1 ? 's' : ''}
											</Text>
										</>
									)}
								</TouchableOpacity>
							</View>
						</View>
					</ScrollView>
				</View>
			</Modal>

			{/* Sent Messages Modal */}
			<Modal
				visible={showSentModal}
				transparent
				animationType="fade"
				onRequestClose={() => closeSentModal()}
			>
				<View style={{
					flex: 1,
					backgroundColor: 'rgba(0,0,0,0.6)',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 20
				}}>
					<View style={{
						backgroundColor: colors.cream,
						borderRadius: 24,
						padding: isMobile ? 20 : 28,
						width: '100%',
						maxWidth: 520,
						borderWidth: 1,
						borderColor: colors.border
					}}>
						<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
							<View>
								<Text style={{ fontSize: isMobile ? 20 : 22, fontFamily: fonts.heading, color: colors.text }}>{sentModalGifter?.name || 'Sent Messages'}</Text>
								<Text style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>{sentModalGifter?.email || sentModalGifter?.phone || ''}</Text>
							</View>
							<TouchableOpacity onPress={closeSentModal} style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: colors.creamDark, justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: colors.border }}>
								<Ionicons name="close" size={20} color={colors.textMuted} />
							</TouchableOpacity>
						</View>
						<ScrollView style={{ maxHeight: 320 }}>
							{sentModalCards.length === 0 ? (
								<Text style={{ fontSize: 14, color: colors.textMuted }}>No sent messages yet.</Text>
							) : sentModalCards.map((card, i) => (
								<View key={i} style={{ marginBottom: 16, padding: 12, backgroundColor: colors.white, borderRadius: 12, borderWidth: 1, borderColor: colors.border }}>
									<Text style={{ fontSize: 12, color: colors.textMuted }}>{formatDate(card.sentAt || card.sentDate)}</Text>
									<Text style={{ marginTop: 6, fontSize: 14, color: colors.text }}>{card.message || (card.personalizedMessages && card.personalizedMessages[0] && card.personalizedMessages[0].message) || ''}</Text>
								</View>
								))}
						</ScrollView>
					</View>
				</View>
			</Modal>
			{/* Bulk Message Modal (Legacy - redirects to unified) */}
			<Modal
				visible={showBulkMessageModal}
				transparent
				animationType="fade"
				onRequestClose={() => setShowBulkMessageModal(false)}
			>
				<View style={{
					flex: 1,
					backgroundColor: 'rgba(0,0,0,0.6)',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 20
				}}>
					<View style={{
						backgroundColor: colors.cream,
						borderRadius: 24,
						padding: 28,
						width: '100%',
						maxWidth: 500,
						shadowColor: '#1a5c3a',
						shadowOffset: { width: 0, height: 10 },
						shadowOpacity: 0.2,
						shadowRadius: 30,
						borderWidth: 1,
						borderColor: colors.border
					}}>
						{/* Modal Header */}
						{(() => {
							const uniqueRecipients = getUniqueRecipients();
							const totalGiftsSelected = uniqueRecipients.reduce((sum, r) => sum + r.gifts.length, 0);
							return (
								<>
									<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
										<View>
											<Text style={{ fontSize: 26, fontFamily: fonts.heading, color: colors.text }}>
												Send Thank You
											</Text>
											<Text style={{ fontSize: 15, fontFamily: fonts.body, color: colors.textMuted, marginTop: 4 }}>
												{uniqueRecipients.length} recipient{uniqueRecipients.length !== 1 ? 's' : ''} • {totalGiftsSelected} gift{totalGiftsSelected !== 1 ? 's' : ''}
											</Text>
										</View>
										<TouchableOpacity
											onPress={() => setShowBulkMessageModal(false)}
											style={{
												width: 40,
												height: 40,
												borderRadius: 20,
												backgroundColor: colors.creamDark,
												justifyContent: 'center',
												alignItems: 'center',
												borderWidth: 1,
												borderColor: colors.border
											}}
										>
											<Text style={{ fontSize: 18, color: colors.textMuted }}>✕</Text>
										</TouchableOpacity>
									</View>

									{/* Recipients Preview - Unique people only */}
									<View style={{
										flexDirection: 'row',
										flexWrap: 'wrap',
										gap: 8,
										marginBottom: 20
									}}>
										{uniqueRecipients.slice(0, 5).map((recipient, i) => {
											const ac = getAvatarColor(recipient.name);
											return (
												<View key={i} style={{
													flexDirection: 'row',
													alignItems: 'center',
													backgroundColor: colors.creamDark,
													paddingLeft: 4,
													paddingRight: 12,
													paddingVertical: 4,
													borderRadius: 20,
													gap: 8,
													borderWidth: 1,
													borderColor: colors.border
												}}>
													{recipient.photoURL ? (
														<Image 
															source={{ uri: recipient.photoURL }} 
															style={{ width: 28, height: 28, borderRadius: 14 }}
														/>
													) : (
														<View style={{
															width: 28,
															height: 28,
															borderRadius: 14,
															backgroundColor: ac.bg,
															justifyContent: 'center',
															alignItems: 'center'
														}}>
															<Text style={{ fontSize: 11, fontFamily: fonts.body, fontWeight: '600', color: ac.text }}>
																{getInitials(recipient.name)}
															</Text>
														</View>
													)}
													<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.text }}>
														{recipient.name}
														{recipient.gifts.length > 1 && (
															<Text style={{ color: colors.textMuted }}> ({recipient.gifts.length} gifts)</Text>
														)}
													</Text>
												</View>
											);
										})}
										{uniqueRecipients.length > 5 && (
											<View style={{
												backgroundColor: colors.creamDark,
												paddingHorizontal: 12,
												paddingVertical: 8,
												borderRadius: 20,
												borderWidth: 1,
												borderColor: colors.border
											}}>
												<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textLight, fontWeight: '500' }}>
													+{uniqueRecipients.length - 5} more
												</Text>
											</View>
										)}
									</View>

									{/* Message Input */}
									<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '500', color: colors.text, marginBottom: 8 }}>
										Your Message
									</Text>
									<TextInput
										style={{
											backgroundColor: colors.white,
											borderWidth: 1,
											borderColor: colors.border,
											borderRadius: 16,
											padding: 16,
											fontSize: 15,
											fontFamily: fonts.body,
											minHeight: 140,
											textAlignVertical: 'top',
											color: colors.text,
											lineHeight: 24
										}}
										placeholder="Thank you so much for your generous gift! Your thoughtfulness means the world to me..."
										placeholderTextColor={colors.textMuted}
										value={bulkMessage}
										onChangeText={setBulkMessage}
										multiline
									/>

									{/* Delivery Info */}
									<View style={{
										flexDirection: 'row',
										gap: 16,
										marginTop: 16,
										padding: 14,
										backgroundColor: colors.creamDark,
										borderRadius: 12,
										borderWidth: 1,
										borderColor: colors.border
									}}>
										<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
											<Text style={{ fontSize: 16 }}>📧</Text>
											<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textLight }}>
												{uniqueRecipients.filter(r => r.email).length} email{uniqueRecipients.filter(r => r.email).length !== 1 ? 's' : ''}
											</Text>
										</View>
										<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
											<Text style={{ fontSize: 16 }}>💬</Text>
											<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textLight }}>
												{uniqueRecipients.filter(r => r.phone && !r.email).length} SMS
											</Text>
										</View>
									</View>
								</>
							);
						})()}

						{/* Actions */}
						<View style={{ flexDirection: 'row', gap: 12, marginTop: 24 }}>
							<TouchableOpacity
								onPress={() => setShowBulkMessageModal(false)}
								style={{
									flex: 1,
									paddingVertical: 16,
									borderRadius: 14,
									backgroundColor: colors.creamDark,
									alignItems: 'center',
									borderWidth: 1,
									borderColor: colors.border
								}}
							>
								<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
									Cancel
								</Text>
							</TouchableOpacity>
							
							<TouchableOpacity
								onPress={handleBulkSend}
								disabled={!bulkMessage.trim() || isSending}
								style={{
									flex: 2,
									paddingVertical: 16,
									borderRadius: 14,
									backgroundColor: bulkMessage.trim() ? colors.primary : colors.creamDark,
									alignItems: 'center',
									flexDirection: 'row',
									justifyContent: 'center',
									gap: 8,
									borderWidth: bulkMessage.trim() ? 0 : 1,
									borderColor: colors.border
								}}
							>
								{isSending ? (
									<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.white }}>
										Sending...
									</Text>
								) : (
									<>
										<Text style={{ fontSize: 18 }}>✉️</Text>
										<Text style={{ 
											fontSize: 16, 
											fontFamily: fonts.body,
											fontWeight: '600', 
											color: bulkMessage.trim() ? colors.white : colors.textMuted 
										}}>
											Send Thank You
										</Text>
									</>
								)}
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</Modal>

			{/* Individual Thank You Modal */}
			<Modal
				visible={showIndividualModal}
				transparent
				animationType="fade"
				onRequestClose={() => setShowIndividualModal(false)}
			>
				<View style={{
					flex: 1,
					backgroundColor: 'rgba(0,0,0,0.6)',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 20
				}}>
					<View style={{
						backgroundColor: colors.cream,
						borderRadius: 24,
						padding: 28,
						width: '100%',
						maxWidth: 480,
						shadowColor: '#1a5c3a',
						shadowOffset: { width: 0, height: 10 },
						shadowOpacity: 0.2,
						shadowRadius: 30,
						borderWidth: 1,
						borderColor: colors.border
					}}>
						{/* Modal Header */}
						<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
							<Text style={{ fontSize: 24, fontFamily: fonts.heading, color: colors.text }}>
								Send Thank You
							</Text>
							<TouchableOpacity
								onPress={() => {
									setShowIndividualModal(false);
									setSelectedGiftForThankYou(null);
									setIndividualMessage('');
								}}
								style={{
									width: 36,
									height: 36,
									borderRadius: 18,
									backgroundColor: colors.creamDark,
									justifyContent: 'center',
									alignItems: 'center',
									borderWidth: 1,
									borderColor: colors.border
								}}
							>
								<Text style={{ fontSize: 18, color: colors.textMuted }}>✕</Text>
							</TouchableOpacity>
						</View>

						{/* Recipient Info */}
						{selectedGiftForThankYou && (
							<View style={{
								backgroundColor: colors.creamDark,
								borderRadius: 12,
								padding: 16,
								marginBottom: 20,
								borderWidth: 1,
								borderColor: colors.border
							}}>
								<View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
									<View style={{
										width: 48,
										height: 48,
										borderRadius: 24,
										backgroundColor: getAvatarColor(selectedGiftForThankYou.name).bg,
										justifyContent: 'center',
										alignItems: 'center'
									}}>
										<Text style={{ 
											fontSize: 16, 
											fontFamily: fonts.body, 
											fontWeight: '600', 
											color: getAvatarColor(selectedGiftForThankYou.name).text 
										}}>
											{getInitials(selectedGiftForThankYou.name)}
										</Text>
									</View>
									<View style={{ flex: 1 }}>
										<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
											{selectedGiftForThankYou.name}
										</Text>
										<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textMuted, marginTop: 2 }}>
											{selectedGiftForThankYou.email || selectedGiftForThankYou.phone}
										</Text>
									</View>
									<View style={{
										backgroundColor: colors.primaryLight,
										paddingHorizontal: 12,
										paddingVertical: 6,
										borderRadius: 8
									}}>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.primary }}>
											{selectedGiftForThankYou.amount}
										</Text>
									</View>
								</View>
								<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textLight, marginTop: 10 }}>
									{selectedGiftForThankYou.giftDescription}
									{selectedGiftForThankYou.message && ` • "${selectedGiftForThankYou.message}"`}
								</Text>
							</View>
						)}

						{/* Message Input */}
						<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '600', color: colors.text, marginBottom: 8 }}>
							Your Message
						</Text>
						<TextInput
							style={{
								backgroundColor: colors.white,
								borderWidth: 1,
								borderColor: colors.border,
								borderRadius: 14,
								padding: 16,
								fontSize: 15,
								fontFamily: fonts.body,
								minHeight: 120,
								textAlignVertical: 'top',
								color: colors.text,
								lineHeight: 24
							}}
							placeholder="Thank you so much for your generous gift! Your thoughtfulness means the world to me..."
							placeholderTextColor={colors.textMuted}
							value={individualMessage}
							onChangeText={setIndividualMessage}
							multiline
						/>

						{/* Delivery Method */}
						{selectedGiftForThankYou && (
							<View style={{
								flexDirection: 'row',
								alignItems: 'center',
								gap: 8,
								marginTop: 12,
								padding: 10,
								backgroundColor: colors.creamDark,
								borderRadius: 8,
								borderWidth: 1,
								borderColor: colors.border
							}}>
								<Ionicons 
									name={selectedGiftForThankYou.email ? "mail" : "chatbubble"} 
									size={16} 
									color={colors.textLight} 
								/>
								<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textLight }}>
									Will be sent via {selectedGiftForThankYou.email ? 'email' : 'SMS'}
								</Text>
							</View>
						)}

						{/* Actions */}
						<View style={{ flexDirection: 'row', gap: 12, marginTop: 20 }}>
							<TouchableOpacity
								onPress={() => {
									setShowIndividualModal(false);
									setSelectedGiftForThankYou(null);
									setIndividualMessage('');
								}}
								style={{
									flex: 1,
									paddingVertical: 14,
									borderRadius: 12,
									backgroundColor: colors.creamDark,
									alignItems: 'center',
									borderWidth: 1,
									borderColor: colors.border
								}}
							>
								<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
									Cancel
								</Text>
							</TouchableOpacity>
							
							<TouchableOpacity
								onPress={handleSendIndividualThankYou}
								disabled={!individualMessage.trim() || isSending}
								style={{
									flex: 2,
									paddingVertical: 14,
									borderRadius: 12,
									backgroundColor: individualMessage.trim() ? colors.primary : colors.creamDark,
									alignItems: 'center',
									flexDirection: 'row',
									justifyContent: 'center',
									gap: 8,
									borderWidth: individualMessage.trim() ? 0 : 1,
									borderColor: colors.border
								}}
							>
								{isSending ? (
									<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '600', color: colors.white }}>
										Sending...
									</Text>
								) : (
									<>
										<Ionicons name="send" size={16} color={individualMessage.trim() ? colors.white : colors.textMuted} />
										<Text style={{ 
											fontSize: 15, 
											fontFamily: fonts.body,
											fontWeight: '600', 
											color: individualMessage.trim() ? colors.white : colors.textMuted 
										}}>
											Send Thank You
										</Text>
									</>
								)}
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</Modal>

			{/* Re-send Confirmation Modal */}
			<Modal
				visible={showResendConfirm}
				transparent
				animationType="fade"
				onRequestClose={cancelResend}
			>
				<View style={{
					flex: 1,
					backgroundColor: 'rgba(0,0,0,0.6)',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 20
				}}>
					<View style={{
						backgroundColor: colors.cream,
						borderRadius: 20,
						padding: 24,
						width: '100%',
						maxWidth: 400,
						shadowColor: '#1a5c3a',
						shadowOffset: { width: 0, height: 10 },
						shadowOpacity: 0.2,
						shadowRadius: 30,
						borderWidth: 1,
						borderColor: colors.border
					}}>
						{/* Warning Icon */}
						<View style={{
							width: 56,
							height: 56,
							borderRadius: 28,
							backgroundColor: colors.warningLight,
							justifyContent: 'center',
							alignItems: 'center',
							alignSelf: 'center',
							marginBottom: 16
						}}>
							<Ionicons name="alert" size={28} color={colors.warning} />
						</View>

						<Text style={{ 
							fontSize: 20, 
							fontFamily: fonts.heading, 
							color: colors.text, 
							textAlign: 'center',
							marginBottom: 8
						}}>
							Send Another Card?
						</Text>
						
						<Text style={{ 
							fontSize: 15, 
							fontFamily: fonts.body, 
							color: colors.textLight, 
							textAlign: 'center',
							lineHeight: 22,
							marginBottom: 24
						}}>
							You've already sent a thank you card to{' '}
							<Text style={{ fontWeight: '600', color: colors.text }}>
								{pendingResendGift?.name}
							</Text>
							. Are you sure you want to send another one?
						</Text>

						{/* Actions */}
						<View style={{ flexDirection: 'row', gap: 12 }}>
							<TouchableOpacity
								onPress={cancelResend}
								style={{
									flex: 1,
									paddingVertical: 14,
									borderRadius: 12,
									backgroundColor: colors.creamDark,
									alignItems: 'center',
									borderWidth: 1,
									borderColor: colors.border
								}}
							>
								<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
									Cancel
								</Text>
							</TouchableOpacity>
							
							<TouchableOpacity
								onPress={confirmResend}
								style={{
									flex: 1,
									paddingVertical: 14,
									borderRadius: 12,
									backgroundColor: colors.secondary,
									alignItems: 'center'
								}}
							>
								<Text style={{ fontSize: 15, fontFamily: fonts.body, fontWeight: '600', color: colors.white }}>
									Yes, Send Again
								</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</Modal>

			{/* Preview Modal with Templates */}
			<Modal
				visible={showPreviewModal}
				transparent
				animationType="fade"
				onRequestClose={() => setShowPreviewModal(false)}
			>
				<View style={{
					flex: 1,
					backgroundColor: 'rgba(0,0,0,0.6)',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 20
				}}>
					<View style={{
						backgroundColor: colors.cream,
						borderRadius: 24,
						padding: 28,
						width: '100%',
						maxWidth: 600,
						maxHeight: '90%',
						shadowColor: '#1a5c3a',
						shadowOffset: { width: 0, height: 10 },
						shadowOpacity: 0.2,
						shadowRadius: 30,
						borderWidth: 1,
						borderColor: colors.border
					}}>
						{/* Header */}
						<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
							<View>
								<Text style={{ fontSize: 26, fontFamily: fonts.heading, color: colors.text }}>
									Preview & Send
								</Text>
								<Text style={{ fontSize: 15, fontFamily: fonts.body, color: colors.textMuted, marginTop: 4 }}>
									{previewRecipients.length} recipient{previewRecipients.length !== 1 ? 's' : ''}
								</Text>
							</View>
							<TouchableOpacity
								onPress={() => setShowPreviewModal(false)}
								style={{
									width: 40,
									height: 40,
									borderRadius: 20,
									backgroundColor: colors.creamDark,
									justifyContent: 'center',
									alignItems: 'center',
									borderWidth: 1,
									borderColor: colors.border
								}}
							>
								<Text style={{ fontSize: 18, color: colors.textMuted }}>✕</Text>
							</TouchableOpacity>
						</View>

						<ScrollView style={{ maxHeight: 500 }} showsVerticalScrollIndicator={false}>
							{/* Recipients List */}
							<View style={{ marginBottom: 20 }}>
								<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
									Recipients
								</Text>
								<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
									{previewRecipients.slice(0, 8).map((gift, i) => {
										const ac = getAvatarColor(gift.name);
										return (
											<View key={i} style={{
												flexDirection: 'row',
												alignItems: 'center',
												backgroundColor: colors.creamDark,
												paddingLeft: 4,
												paddingRight: 12,
												paddingVertical: 4,
												borderRadius: 20,
												gap: 8,
												borderWidth: 1,
												borderColor: colors.border
											}}>
												<View style={{
													width: 28,
													height: 28,
													borderRadius: 14,
													backgroundColor: ac.bg,
													justifyContent: 'center',
													alignItems: 'center'
												}}>
													<Text style={{ fontSize: 11, fontFamily: fonts.body, fontWeight: '600', color: ac.text }}>
														{getInitials(gift.name)}
													</Text>
												</View>
												<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.text }}>{formatShortName(gift.name)}</Text>
											</View>
										);
									})}
									{previewRecipients.length > 8 && (
										<View style={{
											backgroundColor: colors.creamDark,
											paddingHorizontal: 12,
											paddingVertical: 8,
											borderRadius: 20,
											borderWidth: 1,
											borderColor: colors.border
										}}>
											<Text style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textLight, fontWeight: '500' }}>
												+{previewRecipients.length - 8} more
											</Text>
										</View>
									)}
								</View>
							</View>

							{/* Message Templates */}
							<View style={{ marginBottom: 20 }}>
								<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
									Choose a Template
								</Text>
								<View style={{ gap: 10 }}>
									{messageTemplates.map((template, idx) => (
										<TouchableOpacity
											key={idx}
											onPress={() => {
												setSelectedTemplate(idx);
												setBulkMessage(template.getMessage('[Name]', ''));
											}}
											style={{
												backgroundColor: selectedTemplate === idx ? colors.primaryLight : colors.white,
												borderRadius: 14,
												padding: 16,
												borderWidth: 2,
												borderColor: selectedTemplate === idx ? colors.primary : colors.border
											}}
										>
											<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
												<Text style={{ 
													fontSize: 16, 
													fontFamily: fonts.body, 
													fontWeight: '600', 
													color: selectedTemplate === idx ? colors.primary : colors.text 
												}}>
													{template.name}
												</Text>
												{selectedTemplate === idx && (
													<Ionicons name="checkmark-circle" size={20} color={colors.primary} />
												)}
											</View>
											<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted }} numberOfLines={2}>
												{template.preview}
											</Text>
										</TouchableOpacity>
									))}
								</View>
							</View>

							{/* Message Preview/Edit */}
							<View style={{ marginBottom: 20 }}>
								<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
									Your Message (Editable)
								</Text>
								<TextInput
									style={{
										backgroundColor: colors.white,
										borderWidth: 1,
										borderColor: colors.border,
										borderRadius: 16,
										padding: 16,
										fontSize: 15,
										fontFamily: fonts.body,
										minHeight: 160,
										textAlignVertical: 'top',
										color: colors.text,
										lineHeight: 24
									}}
									value={bulkMessage}
									onChangeText={setBulkMessage}
									multiline
									placeholder="Write your thank you message..."
									placeholderTextColor={colors.textMuted}
								/>
								<Text style={{ fontSize: 12, fontFamily: fonts.body, color: colors.textMuted, marginTop: 8 }}>
									💡 Tip: [Name] will be replaced with each recipient's name automatically
								</Text>
							</View>

							{/* Sample Preview */}
							{previewRecipients.length > 0 && bulkMessage && (
								<View style={{ marginBottom: 20 }}>
									<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
										Sample Preview
									</Text>
									<View style={{
										backgroundColor: colors.white,
										borderRadius: 14,
										padding: 16,
										borderWidth: 1,
										borderColor: colors.border
									}}>
										<View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: colors.border }}>
											<View style={{
												width: 36,
												height: 36,
												borderRadius: 18,
												backgroundColor: getAvatarColor(previewRecipients[0]?.name).bg,
												justifyContent: 'center',
												alignItems: 'center'
											}}>
												<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: getAvatarColor(previewRecipients[0]?.name).text }}>
													{getInitials(previewRecipients[0]?.name)}
												</Text>
											</View>
											<View>
												<Text style={{ fontSize: 14, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
													To: {previewRecipients[0]?.name}
												</Text>
												<Text style={{ fontSize: 12, fontFamily: fonts.body, color: colors.textMuted }}>
													{previewRecipients[0]?.email || previewRecipients[0]?.phone}
												</Text>
											</View>
										</View>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.text, lineHeight: 22 }}>
											{bulkMessage.replace(/\[Name\]/g, previewRecipients[0]?.name?.split(' ')[0] || 'Friend')}
										</Text>
									</View>
								</View>
							)}
						</ScrollView>

						{/* Delivery Info */}
						<View style={{
							flexDirection: 'row',
							gap: 16,
							marginTop: 16,
							padding: 14,
							backgroundColor: colors.creamDark,
							borderRadius: 12,
							borderWidth: 1,
							borderColor: colors.border
						}}>
							<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
								<Text style={{ fontSize: 16 }}>📧</Text>
								<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textLight }}>
									{previewRecipients.filter(g => g.email).length} emails
								</Text>
							</View>
							<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
								<Text style={{ fontSize: 16 }}>💬</Text>
								<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textLight }}>
									{previewRecipients.filter(g => g.phone && !g.email).length} SMS
								</Text>
							</View>
						</View>

						{/* Actions */}
						<View style={{ flexDirection: 'row', gap: 12, marginTop: 20 }}>
							<TouchableOpacity
								onPress={() => setShowPreviewModal(false)}
								style={{
									flex: 1,
									paddingVertical: 16,
									borderRadius: 14,
									backgroundColor: colors.creamDark,
									alignItems: 'center',
									borderWidth: 1,
									borderColor: colors.border
								}}
							>
								<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.text }}>
									Cancel
								</Text>
							</TouchableOpacity>
							
							<TouchableOpacity
								onPress={() => {
									setShowPreviewModal(false);
									handleBulkSend();
								}}
								disabled={!bulkMessage.trim() || isSending}
								style={{
									flex: 2,
									paddingVertical: 16,
									borderRadius: 14,
									backgroundColor: bulkMessage.trim() ? colors.primary : colors.creamDark,
									alignItems: 'center',
									flexDirection: 'row',
									justifyContent: 'center',
									gap: 8,
									borderWidth: bulkMessage.trim() ? 0 : 1,
									borderColor: colors.border
								}}
							>
								{isSending ? (
									<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.white }}>
										Sending...
									</Text>
								) : (
									<>
										<Ionicons name="send" size={18} color={bulkMessage.trim() ? colors.white : colors.textMuted} />
										<Text style={{ 
											fontSize: 16, 
											fontFamily: fonts.body,
											fontWeight: '600', 
											color: bulkMessage.trim() ? colors.white : colors.textMuted 
										}}>
											Send to {previewRecipients.length} recipient{previewRecipients.length !== 1 ? 's' : ''}
										</Text>
									</>
								)}
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</Modal>

			{/* Manual Contribution Modal */}
			<Modal
				visible={showManualContributionModal}
				transparent
				animationType="fade"
				onRequestClose={() => setShowManualContributionModal(false)}
			>
				<View style={{
					flex: 1,
					backgroundColor: 'rgba(0,0,0,0.5)',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 20
				}}>
					<View style={{
						backgroundColor: colors.creamLight,
						borderRadius: 24,
						width: '100%',
						maxWidth: 500,
						maxHeight: '90%',
						shadowColor: '#000',
						shadowOffset: { width: 0, height: 8 },
						shadowOpacity: 0.25,
						shadowRadius: 20,
						elevation: 10
					}}>
						<ScrollView showsVerticalScrollIndicator={false}>
							<View style={{ padding: isMobile ? 24 : 32 }}>
								{/* Header */}
								<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
									<View>
										<Text style={{ fontSize: 24, fontFamily: fonts.heading, color: colors.text }}>
											Add Contribution
										</Text>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginTop: 4 }}>
											Record a gift or cash contribution
										</Text>
									</View>
									<TouchableOpacity
										onPress={() => setShowManualContributionModal(false)}
										style={{
											width: 36,
											height: 36,
											borderRadius: 18,
											backgroundColor: colors.creamDark,
											alignItems: 'center',
											justifyContent: 'center'
										}}
									>
										<Ionicons name="close" size={20} color={colors.textMuted} />
									</TouchableOpacity>
								</View>

								{/* Contribution Type Tabs */}
								<View style={{ flexDirection: 'row', gap: 12, marginBottom: 24 }}>
									<TouchableOpacity
										onPress={() => setManualContributionType('cash')}
										style={{
											flex: 1,
											paddingVertical: 14,
											paddingHorizontal: 16,
											borderRadius: 12,
											backgroundColor: manualContributionType === 'cash' ? colors.primary : colors.creamDark,
											flexDirection: 'row',
											alignItems: 'center',
											justifyContent: 'center',
											gap: 8
										}}
									>
										<Ionicons 
											name="cash-outline" 
											size={20} 
											color={manualContributionType === 'cash' ? '#fff' : colors.textMuted} 
										/>
										<Text style={{ 
											fontSize: 15, 
											fontFamily: fonts.body, 
											fontWeight: '600',
											color: manualContributionType === 'cash' ? '#fff' : colors.textMuted 
										}}>
											Cash Gift
										</Text>
									</TouchableOpacity>
									<TouchableOpacity
										onPress={() => setManualContributionType('gift')}
										style={{
											flex: 1,
											paddingVertical: 14,
											paddingHorizontal: 16,
											borderRadius: 12,
											backgroundColor: manualContributionType === 'gift' ? colors.secondary : colors.creamDark,
											flexDirection: 'row',
											alignItems: 'center',
											justifyContent: 'center',
											gap: 8
										}}
									>
										<Ionicons 
											name="gift-outline" 
											size={20} 
											color={manualContributionType === 'gift' ? '#fff' : colors.textMuted} 
										/>
										<Text style={{ 
											fontSize: 15, 
											fontFamily: fonts.body, 
											fontWeight: '600',
											color: manualContributionType === 'gift' ? '#fff' : colors.textMuted 
										}}>
											Item Gift
										</Text>
									</TouchableOpacity>
								</View>

								{/* Form Fields */}
								<View style={{ gap: 16 }}>
									{/* Contributor Name */}
									<View>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 8 }}>
											Contributor Name (leave blank for anonymous)
										</Text>
										<TextInput
											value={manualContributionData.contributorName}
											onChangeText={(text) => setManualContributionData(prev => ({ ...prev, contributorName: text }))}
											placeholder="John Doe"
											placeholderTextColor={colors.textMuted}
											style={{
												backgroundColor: colors.creamDark,
												borderRadius: 12,
												paddingVertical: 14,
												paddingHorizontal: 16,
												fontSize: 16,
												fontFamily: fonts.body,
												color: colors.text,
												borderWidth: 1,
												borderColor: colors.border
											}}
										/>
									</View>

									{/* Amount */}
									<View>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 8 }}>
											{manualContributionType === 'cash' ? 'Amount' : 'Estimated Value'} *
										</Text>
										<View style={{ flexDirection: 'row', alignItems: 'center' }}>
											<Text style={{ fontSize: 20, fontFamily: fonts.body, color: colors.textMuted, marginRight: 8 }}>$</Text>
											<TextInput
												value={manualContributionData.amount}
												onChangeText={(text) => setManualContributionData(prev => ({ ...prev, amount: text.replace(/[^0-9.]/g, '') }))}
												placeholder="0.00"
												placeholderTextColor={colors.textMuted}
												keyboardType="decimal-pad"
												style={{
													flex: 1,
													backgroundColor: colors.creamDark,
													borderRadius: 12,
													paddingVertical: 14,
													paddingHorizontal: 16,
													fontSize: 16,
													fontFamily: fonts.body,
													color: colors.text,
													borderWidth: 1,
													borderColor: colors.border
												}}
											/>
										</View>
									</View>

									{/* Gift-specific fields */}
									{manualContributionType === 'gift' && (
										<>
											<View>
												<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 8 }}>
													Item Name *
												</Text>
												<TextInput
													value={manualContributionData.itemName}
													onChangeText={(text) => setManualContributionData(prev => ({ ...prev, itemName: text }))}
													placeholder="e.g., Kitchen Mixer"
													placeholderTextColor={colors.textMuted}
													style={{
														backgroundColor: colors.creamDark,
														borderRadius: 12,
														paddingVertical: 14,
														paddingHorizontal: 16,
														fontSize: 16,
														fontFamily: fonts.body,
														color: colors.text,
														borderWidth: 1,
														borderColor: colors.border
													}}
												/>
											</View>
											<View>
												<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 8 }}>
													Description
												</Text>
												<TextInput
													value={manualContributionData.itemDescription}
													onChangeText={(text) => setManualContributionData(prev => ({ ...prev, itemDescription: text }))}
													placeholder="Optional description"
													placeholderTextColor={colors.textMuted}
													style={{
														backgroundColor: colors.creamDark,
														borderRadius: 12,
														paddingVertical: 14,
														paddingHorizontal: 16,
														fontSize: 16,
														fontFamily: fonts.body,
														color: colors.text,
														borderWidth: 1,
														borderColor: colors.border
													}}
												/>
											</View>
										</>
									)}

									{/* Contact Info (optional) */}
									<View>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 8 }}>
											Email (for thank you card)
										</Text>
										<TextInput
											value={manualContributionData.contributorEmail}
											onChangeText={(text) => setManualContributionData(prev => ({ ...prev, contributorEmail: text }))}
											placeholder="email@example.com"
											placeholderTextColor={colors.textMuted}
											keyboardType="email-address"
											autoCapitalize="none"
											style={{
												backgroundColor: colors.creamDark,
												borderRadius: 12,
												paddingVertical: 14,
												paddingHorizontal: 16,
												fontSize: 16,
												fontFamily: fonts.body,
												color: colors.text,
												borderWidth: 1,
												borderColor: colors.border
											}}
										/>
									</View>

									<View>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 8 }}>
											Phone (for thank you card)
										</Text>
										<TextInput
											value={manualContributionData.contributorPhone}
											onChangeText={(text) => setManualContributionData(prev => ({ ...prev, contributorPhone: text }))}
											placeholder="(555) 123-4567"
											placeholderTextColor={colors.textMuted}
											keyboardType="phone-pad"
											style={{
												backgroundColor: colors.creamDark,
												borderRadius: 12,
												paddingVertical: 14,
												paddingHorizontal: 16,
												fontSize: 16,
												fontFamily: fonts.body,
												color: colors.text,
												borderWidth: 1,
												borderColor: colors.border
											}}
										/>
									</View>

									{/* Message */}
									<View>
										<Text style={{ fontSize: 14, fontFamily: fonts.body, color: colors.textMuted, marginBottom: 8 }}>
											Gift Message
										</Text>
										<TextInput
											value={manualContributionData.message}
											onChangeText={(text) => setManualContributionData(prev => ({ ...prev, message: text }))}
											placeholder="Any message from the giver"
											placeholderTextColor={colors.textMuted}
											multiline
											numberOfLines={3}
											style={{
												backgroundColor: colors.creamDark,
												borderRadius: 12,
												paddingVertical: 14,
												paddingHorizontal: 16,
												fontSize: 16,
												fontFamily: fonts.body,
												color: colors.text,
												borderWidth: 1,
												borderColor: colors.border,
												minHeight: 80,
												textAlignVertical: 'top'
											}}
										/>
									</View>
								</View>

								{/* Submit Button */}
								<TouchableOpacity
									onPress={handleSubmitManualContribution}
									disabled={isAddingContribution || !manualContributionData.amount || (manualContributionType === 'gift' && !manualContributionData.itemName)}
									style={{
										marginTop: 24,
										paddingVertical: 16,
										borderRadius: 14,
										backgroundColor: (manualContributionData.amount && (manualContributionType !== 'gift' || manualContributionData.itemName)) 
											? colors.primary 
											: colors.creamDark,
										alignItems: 'center',
										flexDirection: 'row',
										justifyContent: 'center',
										gap: 8
									}}
								>
									{isAddingContribution ? (
										<Text style={{ fontSize: 16, fontFamily: fonts.body, fontWeight: '600', color: colors.white }}>
											Adding...
										</Text>
									) : (
										<>
											<Ionicons 
												name="add-circle" 
												size={20} 
												color={(manualContributionData.amount && (manualContributionType !== 'gift' || manualContributionData.itemName)) 
													? colors.white 
													: colors.textMuted} 
											/>
											<Text style={{ 
												fontSize: 16, 
												fontFamily: fonts.body, 
												fontWeight: '600', 
												color: (manualContributionData.amount && (manualContributionType !== 'gift' || manualContributionData.itemName)) 
													? colors.white 
													: colors.textMuted 
											}}>
												Add {manualContributionType === 'cash' ? 'Cash Gift' : 'Item Gift'}
											</Text>
										</>
									)}
								</TouchableOpacity>
							</View>
						</ScrollView>
					</View>
				</View>
			</Modal>

			{/* Toast Notification */}
			{showToast && (
				<Animated.View style={{
					position: 'absolute',
					bottom: 20,
					left: 20,
					right: 20,
					backgroundColor: colors.primary,
					padding: 16,
					borderRadius: 12,
					flexDirection: 'row',
					alignItems: 'center',
					shadowColor: '#000',
					shadowOffset: { width: 0, height: 4 },
					shadowOpacity: 0.3,
					shadowRadius: 8,
					elevation: 6,
					opacity: toastOpacity
				}}>
					<Ionicons name="checkmark-circle" size={24} color={colors.white} style={{ marginRight: 10 }} />
					<Text style={{ color: colors.white, fontSize: 15, fontFamily: fonts.body, fontWeight: '600', flex: 1 }}>
						{toastMessage}
					</Text>
				</Animated.View>
			)}
		</Animated.View>
	);
};

export default GiftTracking;
